import { promptOf, toCloseLeadRef } from 'src/components/LeadClose/LeadClose.functions';
import { useLeadClose } from 'src/components/LeadClose/useLeadClose.hook';
import { useLocalStorageState, useMemoizedFn } from 'ahooks';
import { AxiosError } from 'axios';
import { buildLeadHandle } from 'src/functions/phone.functions';
import {
  hasLeadLeftLeadbotSession,
  hidesLeadFromViewer,
  isLeadHeldByLeadbot,
} from 'src/functions/leadbotSession.functions';
import { EmojiClickData } from 'emoji-picker-react';
import { uniqBy } from 'lodash';
import moment from 'moment';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DropResult } from 'react-beautiful-dnd';
import {
  buildCdmxDayBoundary,
  resolveDateRange,
} from 'src/functions/date.functions.ts';
import { NIL as NIL_UUID, v4 as uuid } from 'uuid';
import {
  tWapiMedia,
  tWapiTemplateParameter,
} from '../../components/Chat/Chat.types';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import { useACLContext } from '../../providers/ACLProvider/ACL.hook';
import { usePipeModalsContext } from './PipeModalsSlice.context';
import { eDateFilterType } from '../../providers/ModalsProvider/Modals.types';
import { useSocketsContext } from '../../providers/SocketsProvider/Sockets.context';
import {
  eChannelType,
  eLeadOrigin,
} from '../../providers/SocketsProvider/Sockets.enums';
import {
  tLead,
  tLeadCreatedEvent,
  tLeadUpdatedEvent,
} from '../../providers/SocketsProvider/Sockets.types';
import { eAlertType } from '../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../providers/ToastProvider/Toast.hook';
import { useUserContext } from '../../providers/UserProvider/User.context';
import { eUserRole } from '../../providers/UserProvider/User.enums';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context';
import { eLeadbotState } from '../../pages/MessengerV2/Messenger.enum';
import { ENABLE_FUNNEL_BULK_MOVE, CURRENT_FUNNEL_ID_STORAGE_KEY, INBOX_STAGE_NAME, initialStagesForCreatingFunnel, LEAD_MOVED_EVENT, STAGNANT_TICK_INTERVAL_MS, UNCLASSIFIED_UUID, UNDELIVERED_FEATURE_FLAG } from './Pipe.constants';
import { useAlertCenterContext } from '../../providers/AlertCenterProvider/AlertCenter.hook';
import { PipeContext } from './Pipe.context';
import {
  applyFilterMismatchRemoval,
  applyLeadCreatedToStage,
  areStageLeadCountsLoaded,
  applyBulkResultToStages,
  reduceSelection,
  type tBulkSelectionResult,
  EMPTY_SELECTION,
  type tSelectionState,
  type tSelectionAction,
  deriveBoardExpiredState,
  funnelLeadParser,
  funnelParser,
  funnelStageLegacyParser,
  funnelStagesParser,
  funnelUserLegacyParser,
  funnelWriteErrorMessage,
  httpStatusOf,
  mergeEngagementByLead,
  mergeFunnelStageWithPrev,
  reduceFunnelUpdatedEvent,
  pickStagePagination,
  shouldRemoveLeadFromFunnel,
  shouldSeedSlbState,
  resolveStuckLeadsDeepLinkStage,
  STUCK_LEADS_DEEP_LINK_PARAM,
  STUCK_LEADS_DEEP_LINK_VALUE,
  buildBoardFilterParams,
  hasActiveBoardFilters,
  isLeadUndelivered,
  removeLeadFromStages,
  restoreLeadToStages,
  tRemovedBoardLead,
} from './Pipe.functions';
import { isMarkOnlyFrame } from '../MessengerV2/Messenger.functions';
import { useLoadedFeatureFlag } from '../../hooks/featureFlags/useLoadedFeatureFlag.hook';
import { useBoardUndeliveredCount } from './useBoardUndeliveredCount.hook';
import { useFunnelBulkOperations } from './useFunnelBulkOperations.hook';
import { toMatchingQuery } from '../Directory/Directory.matching';
import { DirectoryTabs } from '../Directory/Directory.enum';
import { PipeService } from './Pipe.service';
import {
  addAcknowledgedLeadId,
  loadAcknowledgedLeadIds,
  removeAcknowledgedLeadId,
} from './slbAcknowledgmentStorage';
import {
  DropdownOption,
  tCreateFunnelForm,
  tCreateLeadRequest,
  tEditFunnelFields,
  tFetchTemplatesResponse,
  tFunnel,
  tFunnelLead,
  tFunnelLegacy,
  tFunnelStage,
  tFunnelsWithStages,
  tFunnelUser,
  tGetLeadsQueryParams,
  tCreateStageConfig,
  tPipeContext,
  tTemplates,
  tUpdateExpiresAfterResult,
  tUpdateFunnelRequest,
  tWorkspaceFunnel,
  tBoardFilters,
} from './Pipe.types';
import { tCreateLeadForm } from './components/AddLead/AddLead.types';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RouterPaths } from '../../components/Router/Router.constants';
import { SettingsTabs } from '../../pages/Settings/Settings.enum';
import { useFeatureFlagsContext } from '../../providers/FeatureFlagsProvider/FeatureFlags.context';
// TODO: Restore when aiRespondEnabled validation is re-enabled
import { smartLeadbotConfigService } from '../Settings/components/SmartLeadbotConfig/SmartLeadbotConfig.service';
import { autoRespondService } from '../../services/AI/autoRespond.service';
import { tSlbEngagementState, tSlbPausedReason } from '../../services/AI/AI.types';
import { clearSlbChatMap } from './slbChatMap';
import { onSlbConfigChange } from '../../utils/slbConfigChannel';
import { SmartLeadbotService } from '../Onboarding/components/SmartLeadbot/SmartLeadbot.service';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { eAvailabilityPromptReason } from 'src/hooks/availability/availabilityPrompt.enum';
import { requestAvailabilityPrompt } from 'src/hooks/availability/useAvailabilityPrompt.hook';
import { matchesAssignedUserFilter } from 'src/utils/assignedUserFilter';
import { useChallengeSignal } from 'src/components/ChallengeCenter/ChallengeCenter.signals';
import { leadActiveChangedEvent } from 'src/components/Chat/leadActiveChanged.event';

export const usePipeContext = (): tPipeContext => useContext(PipeContext);

export default function usePipe(): tPipeContext {
  const { addAlert } = useToastContext();
  const { trackEvent } = useAnalytics();
  const { idToken, currentUserId, currentUser, currentUserRole } = useUserContext();
  const leadClose = useLeadClose();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { isSmartLeadBotEnabled } = useFeatureFlagsContext();
  const {
    leadCreatedEvent,
    leadUpdatedEvent,
    leadDeletedEvent,
    funnelCreatedEvent,
    funnelUpdatedEvent,
    funnelDeletedEvent,
    catchUpEvent,
    profilingCompletedEvent,
    slbSessionStateChangedEvent,
    slbEscalatedEvent,
    slbStageAdvancedEvent,
    slbSessionExpiredEvent,
    slbEngagementChangedEvent,
    leadMessageEvent,
  } = useSocketsContext();
  const modals = usePipeModalsContext();
  const {
    messageReadFilter,
    dateRange,
    dateFilterType,
    integrationFilter,
    statusFilter,
    tagsFilter,
    assignedUserFilters,
    undeliveredFilter,
    setUndeliveredFilter,
    setChatModalIsOpen,
    setCurrentFunnelStatus,
    triggerDirectoryReload,
  } = modals;
  const selectedLeadForChatModal = 'selectedLeadForChatModal' in modals ? modals.selectedLeadForChatModal : null;

  // INT-1596: the chip only filters where the mark is rolled out.
  const isUndeliveredEnabled = useLoadedFeatureFlag(UNDELIVERED_FEATURE_FLAG);
  const isUndeliveredFilterOn = isUndeliveredEnabled && undeliveredFilter;
  const boardFilters: tBoardFilters = useMemo(() => ({
    messageReadFilter,
    dateRange,
    dateFilterType,
    integrationFilter,
    statusFilter,
    tagsFilter,
    assignedUserFilters,
    undeliveredFilter: isUndeliveredFilterOn,
  }), [assignedUserFilters, dateFilterType, dateRange, integrationFilter, isUndeliveredFilterOn, messageReadFilter, statusFilter, tagsFilter]);

  // INT-1596: a transient work queue, never carried past this board — not to
  // Chats, not to Directory, not to the next visit.
  useEffect(() => () => setUndeliveredFilter(false), [setUndeliveredFilter]);

  const {
    isAdvancedPlan,
    isGrowthPlan,
    isBusinessPlan,
    canUserAccessWhatsAppApi,
  } = useACLContext();
  const { t: tFunnel } = useTranslation('pipe/funnel');
  const { t: tDirectory } = useTranslation('directory/directory');
  const navigate = useNavigate();

  const defaultChannel = canUserAccessWhatsAppApi()
    ? eChannelType.WHATSAPP_API
    : eChannelType.WHATSAPP_WEB;

  const [currentFunnelID, setCurrentFunnelID] =
    useLocalStorageState<string>(CURRENT_FUNNEL_ID_STORAGE_KEY);
  // INT-945: funnelData cache invalidation moved to FunnelCacheInvalidationProvider
  // so it runs regardless of which page is mounted. Pipe.hook no longer touches
  // the cache — see src/providers/FunnelCacheProvider/FunnelCache.provider.tsx.
  const [cardViewMode, setCardViewMode] = useLocalStorageState<'compact' | 'normal'>(
    'pipeCardViewMode',
    { defaultValue: 'compact' },
  );

  const [currentFunnel, setCurrentFunnel] = useState<tFunnel | null>(null);
  // INT-1596 / INT-1597: N on the chip, asked again from the lead handlers below.
  const {
    count: undeliveredCount,
    scheduleRefresh: scheduleUndeliveredCountRefresh,
  } = useBoardUndeliveredCount({
    enabled: isUndeliveredEnabled,
    workspaceId: currentWorkspaceId,
    funnelId: currentFunnel?.id,
    filterParams: buildBoardFilterParams(boardFilters),
  });

  // ── U8 · the stagnant minute tick (R11, R12 · KTD5, KTD7) ─────────────────
  // `expired` is computed on read, so before this a 10-minute threshold only
  // surfaced on the next fetch — which is what made a sub-hour threshold worse
  // than useless. One interval per mounted board bumps `now`; every card's red
  // state is derived from it. Keyed on the funnel so a switch replaces the
  // interval instead of stacking a second one.
  const [now, setNow] = useState<number>(() => Date.now());
  const {
    reportBoardExpiredLead,
    stageThresholdEditRequest,
    consumeStageThresholdEditRequest,
  } = useAlertCenterContext();
  const signalChallenge = useChallengeSignal();

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, STAGNANT_TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [currentFunnelID]);

  // Server truth OR the predicate: a fetch or `lead.updated` can un-red a card
  // (the lead replied, so the stamp moved), while the tick can only ever add
  // red — `isExpired` is monotonic in `now` for a fixed stamp.
  const boardExpiredState = useMemo(
    () => deriveBoardExpiredState({ stages: currentFunnel?.stages, now }),
    [currentFunnel?.stages, now],
  );

  // KTD7 — published as primitives, not as the memo's object, so the effect
  // fires when the reported card actually changes rather than on every tick.
  const firstExpired = boardExpiredState.firstExpired;

  useEffect(() => {
    reportBoardExpiredLead(
      firstExpired ? { hasExpired: true, ...firstExpired } : { hasExpired: false },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [
    reportBoardExpiredLead,
    firstExpired?.stageId,
    firstExpired?.leadName,
    firstExpired?.elapsedMinutes,
  ]);

  // The signal must not outlive the board that produced it: the explainer is
  // in-page on the funnels surface, and a stale `true` would keep it eligible
  // in the bell after the user has left.
  useEffect(
    () => () => reportBoardExpiredLead({ hasExpired: false }),
    [reportBoardExpiredLead],
  );

  // U9 (KTD6) — the return leg. The explainer's owner/admin CTA has nowhere to
  // navigate: the threshold modal is this page's. The Alert Center parks a
  // request naming the stage it reported red and the board opens exactly what
  // the stage menu opens (`handleStuckLeadsUpdate` in `Stage.component`).
  // `requestId`, not the object: consuming schedules a state update in the
  // PROVIDER, so a `currentFunnel.stages` change landing in the same commit
  // re-runs this effect with the request still non-null. Without the latch it
  // opens the modal twice and consumes twice.
  const handledStageThresholdRequestRef = useRef<number | null>(null);
  useEffect(() => {
    if (!stageThresholdEditRequest) return;
    if (
      handledStageThresholdRequestRef.current ===
      stageThresholdEditRequest.requestId
    ) {
      return;
    }
    const stages = currentFunnel?.stages;
    // The request can land BEFORE the board has its stages: a click from the
    // bell on another page navigates here first. Waiting is correct; consuming
    // now would drop the request on the floor.
    if (!stages?.length) return;

    handledStageThresholdRequestRef.current =
      stageThresholdEditRequest.requestId;
    const stage = stages.find(
      (candidate) => candidate.id === stageThresholdEditRequest.stageId,
    );
    // Consumed either way — a request for a stage this board does not have
    // (the funnel switched, the stage was deleted) must not survive as a
    // landmine that fires on some later board.
    consumeStageThresholdEditRequest();
    if (!stage) return;

    setSelectedStage(stage);
    setStuckLeadsModalIsOpen(true);
  }, [
    stageThresholdEditRequest,
    currentFunnel?.stages,
    consumeStageThresholdEditRequest,
  ]);

  // BUG-807: a monotonic token, not a boolean. The boolean it replaces could
  // latch: the effect below aborts when there is no current funnel, and that
  // abort skipped the only `setWasFunnelUpdated(false)` in the file, so the
  // flag stayed `true` for the life of the page. Every later request then set
  // an already-`true` value — a React bail-out — so the dependency never
  // changed and the effect never ran again. One early return, and catch-up
  // stopped repairing the board for the rest of the session.
  //
  // A counter cannot latch: each request is a new value, so the effect runs
  // once per request regardless of how the previous run ended. This is the
  // idiom every other refresh subscriber in the repo already uses (see
  // `Notification.hook.ts`, `AlertCenter.hook.ts`, `Chat.hook.ts`); this
  // subscriber was the lone holdout.
  const [funnelRefreshToken, setFunnelRefreshToken] = useState<number>(0);
  // BUG-807: the token deliberately allows a second refresh to start while an
  // earlier one is still in flight — that is the whole point, since the old
  // boolean dropped those signals. It also makes interleaving real: two runs
  // race, and an older per-stage response landing last would overwrite the
  // newer board. This ref is the generation guard. A run reads its own token,
  // and abandons itself once a newer one has started rather than writing a
  // stale snapshot over fresh leads.
  const latestFunnelRefreshTokenRef = useRef(0);
  // BUG-807: the resync ref serves two kinds of refresh. A user-initiated
  // one (funnel deleted, stage updated) changes the board's content wholesale,
  // so the spinner is honest. A background resync (socket catch-up, profiling
  // finished) is a repair the operator never asked for — and `Stages` renders a
  // spinner *in place of every column* while `loadingFilteredLeads` is true, so
  // letting it blank the board turns a silent fix into a visible stall. Once
  // catch-up fires on far more reconnects, that stall lands constantly.
  const isBackgroundResyncRef = useRef(false);
  const bulkOperationsMetadataRefreshRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const isFetchingFunnelsRef = useRef(false);
  // RET-1739: a range pick fires two debounce cycles, so two lead batches can
  // run back to back. Only the newest may publish the "counts are loaded"
  // marker — an earlier batch flipping it advertises a settled board while the
  // user's actual range is still loading, and the board-viewed analytics event
  // reads that same flag as "data is ready".
  const leadsFetchGenerationRef = useRef(0);
  const [editMode, setEditMode] = useState(false);
  const [selectedStage, setSelectedStage] = useState<tFunnelStage | null>(null);
  const [deleteStageModalIsOpen, setDeleteStageModalIsOpen] = useState(false);
  const [autoAssignModalIsOpen, setAutoAssignModalIsOpen] = useState(false);
  const [stuckLeadsModalIsOpen, setStuckLeadsModalIsOpen] = useState(false);
  const [stageDescriptionModalIsOpen, setStageDescriptionModalIsOpen] =
    useState<boolean>(false);
  const [createStageModalIsOpen, setCreateStageModalIsOpen] =
    useState<boolean>(false);

  // AGI-412 — deep link from the Home news card ("Leads estancados, ahora en
  // minutos" → `?open=stuck-leads`): open the threshold modal on the first
  // regular stage the moment the board has its stages, exactly what the
  // stage menu's "Marcar lead estancado" opens. Same shape as the Alert
  // Center bridge above: wait for the stages (a click from the Home lands
  // here before the funnel loads), consume once, then strip the param so a
  // refresh does not re-open the modal (`?window=flows` precedent on the WAPI
  // page). Sellers/viewers just land on the board — the helper returns null
  // for them, and the param is still stripped.
  const [searchParams, setSearchParams] = useSearchParams();
  const canEditStagesForDeepLink =
    currentUserRole === eUserRole.OWNER || currentUserRole === eUserRole.ADMIN;
  useEffect(() => {
    if (searchParams.get(STUCK_LEADS_DEEP_LINK_PARAM) !== STUCK_LEADS_DEEP_LINK_VALUE) return;
    const stages = currentFunnel?.stages;
    if (!stages?.length) return;
    const next = new URLSearchParams(searchParams);
    next.delete(STUCK_LEADS_DEEP_LINK_PARAM);
    setSearchParams(next, { replace: true });
    const stage = resolveStuckLeadsDeepLinkStage({
      stages,
      canEditStages: canEditStagesForDeepLink,
    });
    if (!stage) return;
    setSelectedStage(stage);
    setStuckLeadsModalIsOpen(true);
  }, [searchParams, setSearchParams, currentFunnel?.stages, canEditStagesForDeepLink]);
  const [createStageAtIndex, setCreateStageAtIndex] =
    useState<number | null>(null);
  const [bulkMessageWABModalIsOpen, setBulkMessageWABModalIsOpen] =
    useState(false);
  const [deleteFunnelModalIsOpen, setDeleteFunnelModalIsOpen] = useState(false);
  const [createNewFunnelMode, setCreateNewFunnelMode] = useState(false);
  const [workspaceFunnels, setWorkspaceFunnels] = useState<tWorkspaceFunnel[]>(
    [],
  );
  const [
    loadingStagesForDeleteFunnelModal,
    setLoadingStagesForDeleteFunnelModal,
  ] = useState(false);
  const [funnelsWithStages, setFunnelsWithStages] = useState<
    tFunnelsWithStages[] | null
  >(null);
  const [loadingFilteredLeads, setLoadingFilteredLeads] = useState(false);
  // Id of the funnel whose per-stage lead batch has already settled. Tracked as
  // an id rather than a boolean so switching funnels invalidates it without an
  // extra reset effect: the derived flag below compares it to the current funnel.
  const [leadCountsLoadedFunnelId, setLeadCountsLoadedFunnelId] = useState<
    string | null
  >(null);
  // Column-scoped lead selection. Deliberately session-only: persisting it would
  // let a selection outlive the board state it was built against, and a mutated
  // Set passed to a storage setter is a silent no-op under Object.is anyway.
  const [selection, setSelection] = useState<tSelectionState>(EMPTY_SELECTION);
  const [slbWorkspaceEnabled, setSlbWorkspaceEnabled] = useState(false);
  const [slbHasActiveIntegration, setSlbHasActiveIntegration] = useState(false);
  const [slbConfiguredFunnelId, setSlbConfiguredFunnelId] = useState<string | null>(null);
  // AGI-16 (F9): the full bot-active funnel set. Per-funnel Lead Agent UI (the
  // "Lead Agent (N)" badge) keys on membership here, so it lights up on EVERY
  // active funnel — not just the primary slbConfiguredFunnelId. Seeded from the
  // config's funnelIds (fallback to [funnelId] for legacy single-funnel configs).
  const [slbActiveFunnelIds, setSlbActiveFunnelIds] = useState<string[]>([]);
  // Track leads that were explicitly escalated by SLB (human handoff).
  // Separate from aiRespondEnabled so we can distinguish "no session" from "escalated".
  const [slbEscalatedLeadIds, setSlbEscalatedLeadIds] = useState<Set<string>>(new Set());
  // RET-870 — leads the operator has acknowledged via "Atendido". Hides the
  // PENDIENTE chip without removing the lead from slbEscalatedLeadIds, so the
  // kanban keeps pinning it to the top of its stage (see sortLeadsWithEscalatedFirst).
  // Reset on every new escalation/expiration signal: a fresh incident warrants
  // fresh attention, so acknowledgment does not carry across incidents.
  const [slbAcknowledgedLeadIds, setSlbAcknowledgedLeadIds] = useState<Set<string>>(new Set());
  // RET-870 — tombstones for leads the user just cleared from the PENDIENTE set.
  // When getBulkState() runs on funnel reload (e.g. after a switch-and-return), the
  // backend's needsAttention flag may not have caught up with the local take-control
  // action yet. Without a grace window, the re-seed resurrects the chip and makes
  // the user think the action didn't land. We discard suggestions that arrive within
  // RECENTLY_CLEARED_GRACE_MS of a local clear.
  const recentlyClearedEscalatedRef = useRef<Map<string, number>>(new Map());
  const RECENTLY_CLEARED_GRACE_MS = 60_000;
  const markLeadRecentlyCleared = useCallback((leadId: string) => {
    recentlyClearedEscalatedRef.current.set(leadId, Date.now());
  }, []);
  const wasLeadRecentlyCleared = useCallback((leadId: string) => {
    const stamp = recentlyClearedEscalatedRef.current.get(leadId);
    if (!stamp) return false;
    if (Date.now() - stamp < RECENTLY_CLEARED_GRACE_MS) return true;
    recentlyClearedEscalatedRef.current.delete(leadId);
    return false;
  }, []);
  // Operator explicitly marked the escalation as "Atendido". Does NOT remove
  // the lead from slbEscalatedLeadIds — keeps the kanban sort pinning so the
  // lead stays visible in the same position — only suppresses the chip.
  // Persisted to localStorage with a 2h TTL (see slbAcknowledgmentStorage).
  // RET-870 TEMPORARY — remove persistence when RET-908 makes the backend's
  // needsAttention flag reflect operator-replied, rendering this state moot.
  const acknowledgeSlbEscalatedLead = useCallback((leadId: string) => {
    if (!leadId) return;
    setSlbAcknowledgedLeadIds((prev) => {
      if (prev.has(leadId)) return prev;
      const next = new Set(prev);
      next.add(leadId);
      return next;
    });
    if (currentWorkspaceId) addAcknowledgedLeadId(currentWorkspaceId, leadId);
  }, [currentWorkspaceId]);
  // Helper: every path that flags a lead as pending clears any prior
  // acknowledgment so the chip re-surfaces for the new incident.
  const forgetAcknowledgment = useCallback((leadId: string) => {
    setSlbAcknowledgedLeadIds((prev) => {
      if (!prev.has(leadId)) return prev;
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
    if (currentWorkspaceId) removeAcknowledgedLeadId(currentWorkspaceId, leadId);
  }, [currentWorkspaceId]);
  // Hydrate the acknowledged set from localStorage whenever the workspace
  // changes. loadAcknowledgedLeadIds() also prunes entries older than TTL
  // in-place, so stale data does not accumulate.
  useEffect(() => {
    if (!currentWorkspaceId) {
      setSlbAcknowledgedLeadIds(new Set());
      return;
    }
    setSlbAcknowledgedLeadIds(loadAcknowledgedLeadIds(currentWorkspaceId));
  }, [currentWorkspaceId]);
  const [slbEngagementByLead, setSlbEngagementByLead] = useState<
    Map<string, { engagementState: tSlbEngagementState; pausedReason?: tSlbPausedReason }>
  >(new Map());
  // Reset on workspace switch so a coincident lead-id across workspaces can't
  // leak the previous workspace's state into the in-between render.
  useEffect(() => {
    setSlbEngagementByLead(new Map());
  }, [currentWorkspaceId]);
  const updateEngagementForLead = useCallback(
    (leadId: string, engagementState: tSlbEngagementState, pausedReason?: tSlbPausedReason) => {
      if (!leadId) return;
      setSlbEngagementByLead((prev) => {
        const next = new Map(prev);
        next.set(leadId, { engagementState, pausedReason });
        return next;
      });
    },
    [],
  );
  // Map leadId → { chatId, channelType } for the SLB-active chat.
  // Used by Stage.handleLeadClick to override the chat selection so the modal
  // opens the correct conversation (the one where SLB is active).
  const [slbLeadChatMap, setSlbLeadChatMap] = useState<Map<string, { chatId: string; channelType: string }>>(new Map());
  const [loadingLeadsPromise, setLoadingLeadsPromise] =
    useState<Promise<void> | null>(null);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  function shouldIncludeLeadSocketEvent(
    lead: tLeadUpdatedEvent | tLeadCreatedEvent,
  ) {
    if (
      (messageReadFilter === 'read' && !lead.response.message?.read) ||
      (messageReadFilter === 'unread' && lead.response.message?.read)
    )
      return false;

    const isLastAction = dateFilterType === eDateFilterType.LAST_ACTION_AT;
    const responseAny = lead.response as Record<string, unknown>;
    const dateField = isLastAction
      ? (responseAny.last_action_at as string | undefined) ??
        lead.response.created_at
      : lead.response.created_at;

    // RET-1739: match the window the query actually asked for. Testing each
    // end independently left a one-sided range with no upper bound, so a live
    // lead from outside the single day that range now resolves to was still
    // admitted — and counted — while a refetch would drop it. Comparing
    // against the same CDMX day boundaries the request uses also keeps live
    // events and refetches from disagreeing at the edges for users outside
    // CDMX, where browser-local midnight is not the queried boundary.
    const resolvedRange = resolveDateRange(dateRange);

    if (resolvedRange) {
      const { from, to } = resolvedRange;

      if (moment(dateField).isBefore(buildCdmxDayBoundary(from).start))
        return false;

      if (moment(dateField).isAfter(buildCdmxDayBoundary(to).end)) return false;
    }

    if (
      integrationFilter &&
      lead.response.message?.channel_type !== integrationFilter
    )
      return false;

    if (statusFilter && lead.response.status?.uuid !== statusFilter.uuid)
      return false;

    if (
      !matchesAssignedUserFilter(
        lead.response.user?.auth_id,
        assignedUserFilters,
      )
    )
      return false;

    const leadMatchesTagsFilter = tagsFilter?.every((filterTag) =>
      lead.response.tags?.some((leadTag) => leadTag.name === filterTag.name),
    );

    if (tagsFilter && tagsFilter.length > 0 && !leadMatchesTagsFilter)
      return false;

    // INT-1597: the seventh dimension, written a second time here on purpose —
    // the fetch applies it on the server, this applies it to live events. A
    // frame that omits the mark (a stage move, a new message) keeps the card's.
    if (
      isUndeliveredFilterOn &&
      !isLeadUndelivered(lead.response, findBoardCard(lead.response.uuid))
    )
      return false;

    return true;
  }

  /** The card a lead already has on this board, in whichever stage holds it. */
  function findBoardCard(leadId: string): tFunnelLead | undefined {
    for (const stage of currentFunnel?.stages ?? []) {
      const card = stage.leads?.find((lead) => lead.id === leadId);
      if (card) return card;
    }
    return undefined;
  }

  leadCreatedEvent.useSubscription((event) => {
    scheduleUndeliveredCountRefresh();
    if (!shouldIncludeLeadSocketEvent(event)) return;

    // BUG-892 — the same rule the update handler applies, and the same one the server
    // applies. A lead created while the Leadbot holds it belongs on no seller's board; the
    // server already withholds it from them, and this is the client half of that, which also
    // covers the doors that still bypass the hold server-side (the inbox branches).
    if (
      hidesLeadFromViewer({
        currentState: event.response.leadbot_state,
        currentUserRole,
      })
    ) {
      return;
    }

    const workspaceHasInbox = !!currentFunnel?.stages.find(
      (stage) => stage.id === NIL_UUID,
    );
    const newLead = funnelLeadParser(event.response, defaultChannel);

    const shouldIgnoreLead = shouldRemoveLeadFromFunnel({
      currentUserRole,
      currentUserId,
      assignedUserId: event.response.user?.auth_id,
      funnelIsPublic: currentFunnel?.isPublic,
      leadHasFunnel: !!event.response.funnel,
    });

    if (
      (event.response.funnel?.uuid !== currentFunnel?.id &&
        !workspaceHasInbox) ||
      shouldIgnoreLead
    )
      return;

    setCurrentFunnel((prevFunnel) => {
      if (!prevFunnel) return null;

      // Source `workspaceHasInbox` and `currentFunnelId` from `prevFunnel` rather
      // than the outer closure: under rapid funnel switches in concurrent mode,
      // the queued WS callback can land while `currentFunnel` reflects a newer
      // funnel than the state about to be reduced. Same defense the sister
      // reducer `reduceFunnelUpdatedEvent` applies (BUG-460).
      const prevHasInbox = prevFunnel.stages.some((s) => s.id === NIL_UUID);

      const newStages = prevFunnel.stages.map((funnelStage) =>
        applyLeadCreatedToStage({
          stage: funnelStage,
          newLead,
          eventFunnelUuid: event.response.funnel?.uuid,
          eventStageUuid: event.response.stage?.uuid,
          workspaceHasInbox: prevHasInbox,
          currentFunnelId: prevFunnel.id,
        }),
      );

      return {
        ...prevFunnel,
        stages: newStages,
      };
    });
  });

  // LEA-50 (U22): a result saved with "Archivar" takes the card off the board
  // (Ragnarok sends no frame, BUG-1126); "Devolver al embudo" puts it back.
  const archivedFromBoardRef = useRef<Record<string, tRemovedBoardLead>>({});
  leadActiveChangedEvent.useSubscription((change) => {
    if (!leadClose.enabled || !currentFunnel) return;
    if (!change.active) {
      const { removed } = removeLeadFromStages(currentFunnel.stages, change.id);
      if (!removed) return;
      archivedFromBoardRef.current[change.id] = removed;
      setCurrentFunnel((prev) =>
        prev
          ? { ...prev, stages: removeLeadFromStages(prev.stages, change.id).stages }
          : prev,
      );
      return;
    }
    const removed = archivedFromBoardRef.current[change.id];
    if (!removed) return;
    delete archivedFromBoardRef.current[change.id];
    setCurrentFunnel((prev) =>
      prev ? { ...prev, stages: restoreLeadToStages(prev.stages, removed) } : prev,
    );
  });

  leadUpdatedEvent.useSubscription((event) => {
    // INT-1597: any lead update may set or clear a mark — or move, archive or
    // reassign a marked lead — so the chip's N is asked again, throttled.
    scheduleUndeliveredCountRefresh();
    const leftLeadbotSession = hasLeadLeftLeadbotSession({
      previousState: event.response.original?.leadbot_state,
      currentState: event.response.leadbot_state,
      stateChanged: Object.prototype.hasOwnProperty.call(
        event.response.changes ?? {},
        'leadbot_state',
      ),
    });
    if (
      hidesLeadFromViewer({
        currentState: event.response.leadbot_state,
        currentUserRole,
      })
    ) {
      // An open chat on the held lead is the popup's to handle: it swaps to its
      // no-access notice on every route (AGI-761).
      setCurrentFunnel((prevFunnel) => {
        if (!prevFunnel) return null;

        const heldStageUuid =
          event.response.stage?.uuid ?? event.response.original?.stage?.uuid;
        // Removing a card and adjusting a count have different idempotency, so they are
        // triggered differently. Filtering an id that is not there is a no-op, so it runs
        // on *every* held update — that is what stops the bot's own follow-ups from
        // re-inserting the card. A count is not idempotent, so it only moves on the edge
        // into the hold; running it level-triggered subtracted one per follow-up.
        const wasHeldBefore = isLeadHeldByLeadbot(
          event.response.original?.leadbot_state,
        );

        const stages = prevFunnel.stages.map((stage) => {
          const stageLeads = stage.leads ?? [];
          const holdsCard = stageLeads.some(
            (lead) => lead.id === event.response.uuid,
          );
          // The board is paginated, so a lead whose card was never loaded still counts
          // toward its stage's server total. Gating the decrement on `some()` left that
          // count one high forever, so it follows the stage rather than the card.
          const shouldDecrement = !wasHeldBefore && stage.id === heldStageUuid;

          if (!holdsCard && !shouldDecrement) return stage;

          return {
            ...stage,
            leads: holdsCard
              ? stageLeads.filter((lead) => lead.id !== event.response.uuid)
              : stageLeads,
            leadCount: shouldDecrement
              ? Math.max(0, (stage.leadCount ?? 0) - 1)
              : stage.leadCount,
          };
        });
        if (stages.every((stage, index) => stage === prevFunnel.stages[index])) {
          return prevFunnel;
        }
        return { ...prevFunnel, stages };
      });
      return;
    }
    const previousLeadbotState =
      event.response.original?.leadbot_state ?? event.response.leadbot_state;
    // "Was this lead COUNTED before the change?" — deliberately role-blind, because
    // `scopeCountable()` (leads-service) has no role term: `notInLeadbotSession()` excludes a
    // held lead from `leadCount` for every role, superusers included. So this must NOT become
    // role-aware even though superusers now keep held CARDS — a card an admin can see is
    // still not a lead the server counted.
    const wasLeadVisible =
      previousLeadbotState != null
        ? previousLeadbotState !== eLeadbotState.SESSION_ACTIVE
        : false;

    // After updating, the lead should no longer be visible in the funnel as it doesn't match the current filter criteria
    if (!shouldIncludeLeadSocketEvent(event)) {
      setCurrentFunnel((prevFunnel) => {
        if (!prevFunnel) return null;

        const stageToDecrement =
          event.response.original?.stage?.uuid ?? event.response.stage?.uuid;

        const stages = applyFilterMismatchRemoval({
          stages: prevFunnel.stages,
          leadId: event.response.uuid,
          stageToDecrement,
          wasLeadVisible,
        });
        if (stages.every((stage, index) => stage === prevFunnel.stages[index])) {
          return prevFunnel;
        }
        return { ...prevFunnel, stages };
      });

      return;
    }

    const { action_type: actions } = event;
    const updatedLead = event.response;
    const leadInInbox = !updatedLead.stage && !updatedLead.funnel;

    if (
      currentFunnel?.id !== updatedLead.funnel?.uuid &&
      currentFunnel?.id !== updatedLead?.original?.funnel?.uuid &&
      !leadInInbox
    )
      return;

    const leadIsArchived =
      typeof updatedLead.active === 'boolean' && !updatedLead.active;
    const shouldRemoveLead = shouldRemoveLeadFromFunnel({
      currentUserRole,
      currentUserId,
      assignedUserId: updatedLead.user?.auth_id,
      funnelIsPublic: currentFunnel?.isPublic,
      leadHasFunnel: !!updatedLead.funnel,
    });

    if (shouldRemoveLead || leadIsArchived) {
      setCurrentFunnel((prevFunnel) => {
        if (!prevFunnel) return null;

        const newStages = prevFunnel.stages.map((stage) => {
          const stageLeads = stage.leads ?? [];
          const stageHeldTheLead = stageLeads.some(
            (stageLead) => stageLead.id === updatedLead.uuid,
          );

          // BUG-892 review: this branch used to filter the card out of every
          // stage and leave `leadCount` untouched, so the column header kept
          // counting a lead that is no longer on the board. Decrement only the
          // stage that actually held the card — the board is paginated, so a
          // lead absent from `leads` may still be a legitimate part of another
          // stage's total and must not drag it down.
          if (!stageHeldTheLead) {
            return stage;
          }

          return {
            ...stage,
            leads: stageLeads.filter(
              (stageLead) => stageLead.id !== updatedLead.uuid,
            ),
            leadCount: Math.max(0, (stage.leadCount ?? 0) - 1),
          };
        });

        return {
          ...prevFunnel,
          stages: newStages,
        };
      });

      return;
    }

    setCurrentFunnel((prevFunnel) => {
      if (!prevFunnel) return null;

      const newStages = prevFunnel.stages.map((funnelStage) => {
        const { leadCount: funnelStageLeadCount = 0, leads = [] } = funnelStage;

        const safeLeads = leads ?? [];

        const isLeadStageUpdateEvent = !!actions?.includes('changed_stage_id');
        const isLeadAssigneeUpdateEvent =
          !!actions?.includes('changed_user_uuid');
        const isLeadMessageFromMeUpdateEvent = !!actions?.includes(
          'changed_last_message_from_me',
        );
        const isLeadMessageUpdateEvent = !!actions?.includes(
          'changed_last_message_on',
        );
        const isLeadActivatedEvent = !!actions?.includes('changed_active');
        const isLeadToInboxUpdateEvent =
          !updatedLead.funnel && !updatedLead.stage;

        const leadShouldMoveAtTheTop =
          isLeadStageUpdateEvent ||
          isLeadAssigneeUpdateEvent ||
          isLeadMessageUpdateEvent ||
          isLeadMessageFromMeUpdateEvent ||
          isLeadActivatedEvent ||
          isLeadToInboxUpdateEvent;

        // A lead that belongs to this funnel but has no stage renders in the virtual
        // Unclassified column. Without this arm the stage-uuid match below can never be
        // satisfied for it, so a card the session-entry branch removed is never put back —
        // permanent loss until a refetch, not a transient glitch. The sibling predicate
        // `leadExistedInUnclassified` already reads the same shape off `original`.
        const belongsInUnclassified =
          funnelStage.id === UNCLASSIFIED_UUID &&
          !updatedLead.stage &&
          updatedLead.funnel?.uuid === currentFunnel?.id;

        if (
          funnelStage.id === updatedLead.stage?.uuid ||
          belongsInUnclassified ||
          (isLeadToInboxUpdateEvent && funnelStage.name === INBOX_STAGE_NAME)
        ) {
          const matchingLead = safeLeads.find((l) => l.id === updatedLead.uuid);
          let newLead = funnelLeadParser(updatedLead, defaultChannel, matchingLead);
          let leadExist = false;
          let shouldAdd = false;
          let stageLeads = safeLeads.map((prevLead) => {
            if (prevLead.id === newLead.id) {
              leadExist = true;
              newLead = {
                ...newLead,
                hasCalendarEvent: prevLead.hasCalendarEvent,
              };

              return newLead;
            }

            return prevLead;
          });

          if (leadShouldMoveAtTheTop) {
            stageLeads = [newLead, ...stageLeads];
            if (!leadExist) {
              shouldAdd = true;
            }
          }

          /**
           * When a tag filter is active and the lead was not previously in the
           * stage (because it didn't match the filter), add it now that it
           * matches. The lead already passed shouldIncludeLeadSocketEvent, so
           * all active filters are satisfied.
           */
          const isNewlyMatchingTagFilter =
            !leadExist && tagsFilter && tagsFilter.length > 0;
          /**
           * INT-1597: with the chip on, a lead whose mark was just set joins
           * its column. Only the mark-only frame leads-service sends when the
           * mark changes: any other frame of a marked lead not on the board is
           * one the column has not paged to yet, already in its count.
           */
          const isNewlyMarked =
            !leadExist &&
            isUndeliveredFilterOn &&
            isMarkOnlyFrame(updatedLead.changes);

          if (
            !leadShouldMoveAtTheTop &&
            !leadExist &&
            (isNewlyMatchingTagFilter || isNewlyMarked || leftLeadbotSession)
          ) {
            stageLeads = [newLead, ...stageLeads];
            shouldAdd = true;
          }

          stageLeads = uniqBy(stageLeads, 'id');
          const leadCount = shouldAdd
            ? funnelStageLeadCount + 1
            : funnelStageLeadCount;

          return {
            ...funnelStage,
            leads: stageLeads,
            leadCount,
          };
        }

        const leadExistedInSelectedFunnel =
          funnelStage.id === updatedLead.original?.stage?.uuid;
        // BUG-892: without `original` this used to read `!undefined &&
        // !undefined` as "came from the inbox" and decremented every stage.
        // BUG-897: and like its two sibling predicates it must also name the stage being
        // mapped — a lead leaving the Inbox only ever left the Inbox column, so no other
        // stage may drop its count. The two guards are independent and both apply.
        const leadExistedInInbox =
          funnelStage.name === INBOX_STAGE_NAME &&
          updatedLead.original != null &&
          // leads-service emits `'original' => Arr::get($data, 'original', [])`
          // (`LeadsTransformer.php:58`), which serialises to `[]` rather than being absent —
          // and `[] != null` is `true`, so the null check alone never fired and this stayed
          // true for every stage. An array is the "no snapshot" shape; only an object can
          // meaningfully lack `funnel` and `stage`.
          !Array.isArray(updatedLead.original) &&
          !updatedLead.original.funnel &&
          !updatedLead.original.stage;
        const leadExistedInUnclassified =
          funnelStage.id === UNCLASSIFIED_UUID &&
          updatedLead.original?.funnel?.uuid === currentFunnel?.id &&
          !updatedLead.original?.stage;

        // BUG-892: a reveal names the stage the lead is in now, so a card for
        // the same lead still sitting here is stale. Move it instead of leaving
        // a duplicate or skipping the insert: this stage drops it with -1 while
        // the target stage adds it with +1, which keeps every count aligned
        // with the server (the stale card was already counted here).
        const hasStaleRevealedCard =
          leftLeadbotSession &&
          safeLeads.some((stageLead) => stageLead.id === updatedLead.uuid);

        if (hasStaleRevealedCard) {
          return {
            ...funnelStage,
            leads: safeLeads.filter(
              (stageLead) => stageLead.id !== updatedLead.uuid,
            ),
            leadCount: Math.max(0, funnelStageLeadCount - 1),
          };
        }

        // Removing a stale card and adjusting a count are separate decisions with different
        // predicates — the same split the held branch above makes. Filtering an id that is
        // not there is a no-op, so the removal runs for any origin stage the lead left;
        // gating it on `wasLeadVisible` meant a held lead the bot reclassified stayed in its
        // old column *and* appeared in the new one, which is visible to superusers now that
        // they keep held cards. The decrement keeps `wasLeadVisible`, because a held lead was
        // never in the server's count to subtract.
        if (
          isLeadStageUpdateEvent &&
          (leadExistedInSelectedFunnel || leadExistedInInbox || leadExistedInUnclassified)
        ) {
          const shouldSubtract =
            updatedLead.original?.stage?.uuid !== updatedLead.stage?.uuid;
          const leads = safeLeads.filter(
            (stageLead) => stageLead.id !== updatedLead.uuid,
          );

          let finalCount = funnelStageLeadCount;

          // The count follows the CARDS, not the server's countability rule. `leadCount` is
          // the server's total plus every card this client inserted and minus every card it
          // removed — the client shows an admin held cards the server does not count, and
          // increments on each one, so it has to decrement when it takes one away. Keying
          // this on `wasLeadVisible` instead left the same lead counted in both its old and
          // new stage after the bot reclassified it. Removing a card that was never here is
          // a no-op, so the presence check is also what keeps this idempotent.
          if (shouldSubtract && safeLeads.some((l) => l.id === updatedLead.uuid)) {
            finalCount = Math.max(0, funnelStageLeadCount - 1);
          }

          return {
            ...funnelStage,
            leads,
            leadCount: finalCount,
          };
        }

        return funnelStage;
      });

      return {
        ...prevFunnel,
        stages: newStages,
      };
    });
  });

  leadDeletedEvent.useSubscription((event) => {
    scheduleUndeliveredCountRefresh();
    const deletedLeadIds = new Set<string>(event.response);

    // RET-870 — drop any acknowledgment state tied to deleted leads so the
    // localStorage ack set doesn't outlive the lead and accumulate stale ids
    // over the 2h TTL window.
    deletedLeadIds.forEach((leadId) => forgetAcknowledgment(leadId));

    setCurrentFunnel((prevFunnel) => {
      if (!prevFunnel) return null;

      const newStages = prevFunnel.stages.map((funnelStage) => {
        const { leadCount: funnelStageLeadCount = 0, leads = [] } = funnelStage;
        const safeLeads = leads ?? [];

        const newLeads = safeLeads.filter(
          (stageLead) => !deletedLeadIds.has(stageLead.id),
        );
        const removedCount = safeLeads.length - newLeads.length;
        const finalCount = Math.max(0, funnelStageLeadCount - removedCount);

        return {
          ...funnelStage,
          leads: newLeads,
          leadCount: finalCount,
        };
      });

      return {
        ...prevFunnel,
        stages: newStages,
      };
    });
  });

  funnelCreatedEvent.useSubscription((event) => {
    const newFunnel: tWorkspaceFunnel = {
      id: event.response.uuid,
      name: event.response.name,
      isDefault: false,
    };
    setWorkspaceFunnels((prevFunnels) =>
      uniqBy([...prevFunnels, newFunnel], 'id'),
    );
  });

  funnelUpdatedEvent.useSubscription((event) => {
    const updatedFunnel = funnelParser(event.response as tFunnelLegacy);

    // Update workspaceFunnels list if the funnel's metadata (name, emoji, etc.) changed
    // This ensures the funnel list stays in sync even if it's not the current funnel
    setWorkspaceFunnels((prevFunnels) => {
      const funnelIndex = prevFunnels.findIndex(
        (f) => f.id === updatedFunnel.id,
      );
      if (funnelIndex !== -1) {
        const updatedFunnels = [...prevFunnels];
        updatedFunnels[funnelIndex] = {
          ...updatedFunnels[funnelIndex],
          name: updatedFunnel.name,
          // Note: emoji and isDefault might not be in the socket event, preserve existing values
        };
        return updatedFunnels;
      }
      return prevFunnels;
    });

    // funnelData cache invalidation is owned by FunnelCacheInvalidationProvider
    // (INT-945) — this subscriber stays scoped to its remaining page-local
    // responsibilities below: keeping `workspaceFunnels` (above) in sync with
    // funnel metadata changes, and merging the updated funnel into
    // `currentFunnel` for the actively-viewed Pipe page.

    // Guard + merge live in reduceFunnelUpdatedEvent so the stale-closure fix
    // can be asserted via pure-function tests (see Pipe.functions.test.ts).
    // MUST stay as a functional updater — the guard reads from the latest
    // committed state, not a render-captured value.
    setCurrentFunnel((prevValue) =>
      reduceFunnelUpdatedEvent(prevValue, updatedFunnel),
    );
  });

  funnelDeletedEvent.useSubscription((event) => {
    const funnelToDeleteId = event.response.funnels[0];

    if (currentFunnel?.id === funnelToDeleteId) {
      const defaultFunnel = workspaceFunnels.find((funnel) => funnel.isDefault);

      getSelectedFunnelInfo(defaultFunnel || workspaceFunnels[0]);
    }

    setWorkspaceFunnels((prevFunnels) =>
      prevFunnels.filter((prevFunnel) => prevFunnel.id !== funnelToDeleteId),
    );

    isBackgroundResyncRef.current = false;
    setFunnelRefreshToken((prev) => prev + 1);
  });

  const fetchWorkspaceFunnels = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetchingFunnelsRef.current) {
      return;
    }

    try {
      if (currentWorkspaceId && idToken) {
        isFetchingFunnelsRef.current = true;

        const workspaceFunnelsResponse =
          await PipeService.getWorkspaceFunnels(currentWorkspaceId);

        const pipes = workspaceFunnelsResponse.data.pipes;

        // CRITICAL: Always update workspaceFunnels list to reflect latest state
        // This ensures the funnel list stays in sync even when the same funnel is selected
        setWorkspaceFunnels(pipes);

        // If no pipes found, clear current funnel and return early
        if (!pipes || pipes.length === 0) {
          setCurrentFunnel(null);
          setLoading(false);
          isFetchingFunnelsRef.current = false;
          return;
        }

        let defaultFunnel: tWorkspaceFunnel | undefined;

        defaultFunnel = pipes.find((pipe) => pipe.id === currentFunnelID);

        if (!defaultFunnel) {
          defaultFunnel = pipes.find((pipe) => pipe.isDefault) || pipes[0];
        }

        const isSameFunnel = currentFunnel?.id === defaultFunnel?.id;

        // Only load funnel details and show loader if we don't have a current funnel or it's a different funnel
        if (!currentFunnel || !isSameFunnel) {
          // Only set loading if we're actually going to fetch funnel details
          setLoading(true);

          if (defaultFunnel) {
            try {
              const funnelInfoResponse = await PipeService.getFunnelInfo(
                currentWorkspaceId,
                defaultFunnel.id,
              );
              // Ensure all stages have leads initialized as arrays, not null
              const funnelWithInitializedLeads = {
                ...funnelInfoResponse.data,
                stages: funnelInfoResponse.data.stages.map((stage) => ({
                  ...stage,
                  leads: stage.leads || [],
                })),
              };
              setCurrentFunnel(funnelWithInitializedLeads);
              setCurrentFunnelID(funnelInfoResponse.data.id);
              setCurrentFunnelStatus(funnelInfoResponse.data.statuses);
            } catch (funnelErr) {
              addAlert({
                type: eAlertType.ERROR,
                message:
                  (funnelErr as Error).message ||
                  'Error al acceder al servidor para traer la información del embudo seleccionado',
              });
            } finally {
              setLoading(false);
            }
          } else {
            setLoading(false);
          }
        }
        // If same funnel, don't change loading state to avoid unnecessary loader flash
      }
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer la información del embudo seleccionado',
      });
      setLoading(false);
    } finally {
      isFetchingFunnelsRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [currentWorkspaceId, idToken, addAlert, currentFunnelID, currentFunnel]);

  useEffect(() => {
    // Token starts at 0 and only ever increments, so 0 means "no refresh has
    // been requested yet" — this is the initial-mount guard, not a latch.
    if (funnelRefreshToken === 0) return;

    // Claim this generation before any await, so a later run can tell an
    // earlier one it has been superseded.
    latestFunnelRefreshTokenRef.current = funnelRefreshToken;
    const requestedViewKey = boardSelectionViewKey;
    const isSuperseded = () =>
      latestFunnelRefreshTokenRef.current !== funnelRefreshToken ||
      boardSelectionViewKeyRef.current !== requestedViewKey;

    // Captured before the first await. `docs/solutions/logic-errors/
    // websocket-race-overwrites-guard-state-during-await.md` documents this
    // exact failure in this codebase: a socket listener firing during the await
    // would flip the ref, and the branch below would then read the new value.
    const isBackgroundResync = isBackgroundResyncRef.current;

    (async () => {
      await fetchWorkspaceFunnels();

      // RET-1739: no half-open guard here. `buildDateFilterParams` resolves a
      // one-sided range to a single day, so the request is always well-formed
      // and page 1 must fire. The old guard silently skipped the fetch, which
      // left the board rendering stage totals with zero lead cards.
      //
      // BUG-807: this abort is reachable on a boot race — `fetchWorkspaceFunnels`
      // returns immediately while the mount fetch is still in flight, so the
      // await resolves with `currentFunnel` still null. It used to also strand
      // the trigger flag; with a token it costs only this one request, and the
      // next one runs normally. Say so rather than returning silently, because
      // a silent abort and a completed repair looked identical from outside.
      if (!currentFunnel) {
        // `error`, not `warn`, on purpose: `LLog.shouldLog` admits only the
        // error level in a production build, so a warning here would be
        // visible in dev and vitest and nowhere an operator can see it —
        // leaving the abort exactly as undiagnosable in the field as before.
        LLog.error(
          {
            component: LOG_COMPONENTS.UI,
            method: 'funnelRefresh',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Funnel refresh aborted: no current funnel',
          undefined,
          { funnelRefreshToken, isBackgroundResync },
        );
        return;
      }

      // A newer refresh started while the funnel list was loading. Its own run
      // will refetch every stage, so issuing ours would only race it with
      // older data.
      if (isSuperseded()) return;

      // Bulk receipts can finish before their websocket effects arrive. Read
      // committed funnel/stage values as well as refilling the visible pages.
      if (bulkOperationsMetadataRefreshRef.current && currentWorkspaceId) {
        try {
          const response = await PipeService.getFunnelInfo(currentWorkspaceId, currentFunnel.id);
          if (isSuperseded()) return;
          setCurrentFunnel((prev) => reduceFunnelUpdatedEvent(prev, response.data));
          bulkOperationsMetadataRefreshRef.current = false;
        } catch (err) {
          LLog.error({ component: LOG_COMPONENTS.UI, method: 'bulkOperationsRefresh', operation: LOG_OPERATIONS.ERROR },
            'Unable to refresh funnel values after a bulk operation', err as Error);
        }
      }

      if (!isBackgroundResync) setLoadingFilteredLeads(true);

      // Pass the guard down rather than only checking it above: each of these
      // writes state as its own response lands, so a check here would let an
      // older run's late response still overwrite a newer board.
      const results = await Promise.allSettled(
        currentFunnel.stages.map(
          async (stage) => await getMoreLeads(stage.id, 1, { isSuperseded }),
        ),
      );
      if (!isBackgroundResync) setLoadingFilteredLeads(false);

      // A superseded run's stages return null because this run abandoned its
      // own writes, not because anything failed. Reporting those as failures
      // would turn every overlap into a burst of false errors. Clear the
      // spinner first, above, so a superseded user-initiated refresh cannot
      // leave one stuck when the newer run is a background resync.
      if (isSuperseded()) return;

      // BUG-807: a stage whose refetch failed leaves that column stale while
      // the repair reports itself done. `getMoreLeads` catches its own errors
      // — it raises a toast and returns `null` rather than rejecting — so the
      // failure signal is a null result, not a rejected promise. Check both:
      // null is what actually happens today, and the rejected branch keeps
      // this honest if that function is ever changed to propagate.
      const failedStages = results.flatMap((result, index) =>
        result.status === 'rejected' || result.value === null
          ? [
              {
                stageId: currentFunnel.stages[index]?.id,
                reason:
                  result.status === 'rejected'
                    ? (result.reason as Error)
                    : undefined,
              },
            ]
          : [],
      );

      if (failedStages.length > 0) {
        // Per stage, so which column is stale is recoverable. An aggregate
        // count says a repair was incomplete but not where.
        failedStages.forEach(({ stageId, reason }) => {
          LLog.error(
            {
              component: LOG_COMPONENTS.UI,
              method: 'funnelRefresh',
              operation: LOG_OPERATIONS.ERROR,
            },
            `Funnel refresh failed or skipped for stage ${stageId}`,
            reason,
            {
              funnelRefreshToken,
              failedStages: failedStages.length,
              totalStages: currentFunnel.stages.length,
            },
          );
        });
      }
    })();
    // `isBackgroundResyncRef` is deliberately not reset here. Every trigger
    // sets it for its own kind of refresh before incrementing the token, so
    // resetting it on completion would clobber a background request that
    // arrived during the await and make the next run blank the board.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [funnelRefreshToken]);

  // Invalidate the column selection whenever the set it was built against stops
  // being the set on screen. This is a data-integrity guard, not cosmetic: a
  // selection carried across a funnel switch would submit one funnel's ids while
  // the user is looking at another, and a selection carried across a filter
  // change would act on leads the user can no longer see.
  //
  // Guarded on an open selection so the common case costs no extra render.
  useEffect(() => {
    if (selection.stageId === null) return;
    setSelection(EMPTY_SELECTION);
    // Intentionally not depending on `selection` itself — this reacts to the
    // board changing underneath the selection, never to the selection changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFunnel?.id,
    currentUserRole,
    messageReadFilter,
    dateRange,
    dateFilterType,
    integrationFilter,
    statusFilter,
    tagsFilter,
    assignedUserFilters,
    isUndeliveredFilterOn,
  ]);

  const dispatchSelection = useCallback((action: tSelectionAction) => {
    setSelection((prev) => reduceSelection(prev, action));
  }, []);

  const isFunnelBulkMoveEnabled = useLoadedFeatureFlag(ENABLE_FUNNEL_BULK_MOVE);
  const boardSelectionViewKey = JSON.stringify([
    currentWorkspaceId,
    currentFunnel?.id,
    currentUserRole,
    buildBoardFilterParams(boardFilters),
    isUndeliveredFilterOn,
  ]);
  const boardSelectionViewKeyRef = useRef(boardSelectionViewKey);
  boardSelectionViewKeyRef.current = boardSelectionViewKey;
  const selectionStage = currentFunnel?.stages.find((stage) => stage.id === selection.stageId);
  const stageMatchingQuery =
    currentFunnel?.id && selection.stageId && !isUndeliveredFilterOn &&
    selection.stageId !== NIL_UUID && selection.stageId !== UNCLASSIFIED_UUID
      ? toMatchingQuery({
          ...boardFilters,
          selectedTab: DirectoryTabs.ACTIVE,
          selectedFunnelForFilter: currentFunnel.id,
          selectedStageForFilter: selection.stageId,
        })
      : null;
  const bulkOperations = useFunnelBulkOperations({
    enabled: isFunnelBulkMoveEnabled,
    workspaceId: currentWorkspaceId,
    actorId: currentUserId,
    funnelId: currentFunnel?.id,
    stageId: selection.stageId,
    viewKey: boardSelectionViewKey,
    matchingQuery: stageMatchingQuery,
    sourceReady:
      selectionStage?.bulkSelectionViewKey === boardSelectionViewKey &&
      !loadingFilteredLeads && Number.isFinite(selectionStage?.leadCount),
    stageLeadCount: selectionStage?.leadCount,
    loadedLeadIds: selectionStage?.leads?.map((lead) => lead.id) ?? [],
    defaultTitle: (kind) => tDirectory(`ops.title.${kind}Generic`),
    undoTitle: (title) => tDirectory('ops.title.undo', { title }),
    onAccepted: () => setSelection(EMPTY_SELECTION),
    onFinished: () => {
      bulkOperationsMetadataRefreshRef.current = true;
      isBackgroundResyncRef.current = true;
      setFunnelRefreshToken((prev) => prev + 1);
    },
  });

  // Fold a completed bulk selection action into the board. The bulk endpoint
  // returns no lead payloads and nothing refetches the funnel afterwards, so
  // without this the mutation lands server-side while the column keeps showing
  // the old advisor or the old stage until a manual reload.
  const applyBulkSelectionResult = useCallback(
    (result: tBulkSelectionResult) => {
      setCurrentFunnel((prevFunnel) => {
        if (!prevFunnel) return prevFunnel;
        return {
          ...prevFunnel,
          stages: applyBulkResultToStages({
            stages: prevFunnel.stages,
            ...result,
          }),
        };
      });
    },
    [],
  );

  catchUpEvent.useSubscription(() => {
    isBackgroundResyncRef.current = true;
    setFunnelRefreshToken((prev) => prev + 1);
  });

  // Refresh funnel data when profiling finishes so new leads appear without a page reload
  profilingCompletedEvent.useSubscription(() => {
    isBackgroundResyncRef.current = true;
    setFunnelRefreshToken((prev) => prev + 1);
  });

  // Keep kanban chip in sync with SLB session state changes even when no chat is open.
  // AutoRespondToggle.hook covers the open-chat path; this handles all other cases.
  slbSessionStateChangedEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled) return;

    // RET-870 — when funnel_id is present and doesn't match the funnel being viewed,
    // the event is for a session bound to another funnel. Per-lead chat state in
    // currentFunnel.stages is not affected (the lead isn't in this funnel's list),
    // so skip the no-op update. We still update the global slbEscalatedLeadIds
    // marker below so switching to the event's funnel surfaces the correct state.
    // Absent funnel_id (manual toggle, legacy pods) falls back to the prior behavior.
    const isCurrentFunnel = !event.funnel_id || event.funnel_id === currentFunnel?.id;
    if (isCurrentFunnel) {
      updateLeadAiRespondState(event.lead_id, event.enabled);
    }

    if (!event.lead_id) return;
    if (event.enabled) {
      // Re-enabled: clear PENDIENTE and tombstone the id so a subsequent
      // getBulkState() refetch (e.g. after a funnel switch) doesn't resurrect
      // the chip while the backend needsAttention flag is still catching up.
      // Also drop the ack — otherwise a second escalation within the 2h TTL
      // would be silently hidden by the previous cycle's acknowledgment.
      markLeadRecentlyCleared(event.lead_id);
      forgetAcknowledgment(event.lead_id);
      setSlbEscalatedLeadIds((prev) => {
        if (!prev.has(event.lead_id)) return prev;
        const next = new Set(prev);
        next.delete(event.lead_id);
        return next;
      });
      return;
    }

    // RET-870 — only true "needs attention" signals add to PENDIENTE:
    // stage_change (the lead moved into a human-only stage, bot stopped
    // mid-conversation) and escalation events (handled via slbEscalatedEvent).
    // A manual_toggle is the operator voluntarily taking over, not the bot
    // abandoning the lead — per product: "conversaciones que estén en etapas
    // atendidas por el lead agent pero que no estén siendo atendidas, y el
    // último en escribir haya sido el cliente." Voluntary takeovers break
    // criterion #2 but not in a way that warrants the attention marker.
    if (event.reason !== 'manual_toggle') {
      setSlbEscalatedLeadIds((prev) =>
        prev.has(event.lead_id) ? prev : new Set(prev).add(event.lead_id),
      );
      forgetAcknowledgment(event.lead_id);
    } else {
      // Mirror the takeover path: if the operator toggled off manually for
      // a lead that was previously PENDIENTE, clear the chip and ack now so
      // a future bot-driven escalation isn't hidden by a stale 2h ack entry.
      markLeadRecentlyCleared(event.lead_id);
      forgetAcknowledgment(event.lead_id);
      setSlbEscalatedLeadIds((prev) => {
        if (!prev.has(event.lead_id)) return prev;
        const next = new Set(prev);
        next.delete(event.lead_id);
        return next;
      });
    }
  });
  // When a session escalates (human handoff), mark the lead as escalated AND deactivate.
  slbEscalatedEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.workspace_id !== currentWorkspaceId) return;
    if (event.lead_id) {
      updateLeadAiRespondState(event.lead_id, false);
      const leadId = event.lead_id;
      setSlbEscalatedLeadIds((prev) =>
        prev.has(leadId) ? prev : new Set(prev).add(leadId),
      );
      forgetAcknowledgment(event.lead_id);
    }
  });
  // When the AI advances the lead stage during conversation, move the card in real-time.
  slbStageAdvancedEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.workspace_id !== currentWorkspaceId) return;
    moveLeadToStage(event.lead_id, event.next_stage_id);
  });
  // When an SLB session expires (TTL), update the kanban the same way as an escalation.
  slbSessionExpiredEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.workspace_id !== currentWorkspaceId) return;
    if (event.lead_id) {
      updateLeadAiRespondState(event.lead_id, false);
      setSlbEscalatedLeadIds((prev) =>
        prev.has(event.lead_id) ? prev : new Set(prev).add(event.lead_id),
      );
      forgetAcknowledgment(event.lead_id);
    }
  });
  slbEngagementChangedEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.workspace_id !== currentWorkspaceId) return;
    if (event.lead_id) {
      updateEngagementForLead(event.lead_id, event.engagement_state, event.paused_reason);
    }
  });
  // Gate diagnostic events are consumed by MessageBar (per-chat banner), not here.

  // Fallback: reorder lead to top of its stage when a new message arrives.
  // The primary path is lead_event.updated with changed_last_message_on, but if
  // that event is delayed or lost, this ensures the UI stays responsive.
  leadMessageEvent.useSubscription((event) => {
    if (event.workspace_id !== currentWorkspaceId) return;

    setCurrentFunnel((prevFunnel) => {
      if (!prevFunnel) return null;

      const leadUuid = event.lead_uuid;
      let foundInStage = false;

      const newStages = prevFunnel.stages.map((stage) => {
        const leads = stage.leads ?? [];
        const leadIndex = leads.findIndex((lead) => lead.id === leadUuid);

        if (leadIndex < 0) return stage;

        foundInStage = true;
        const lead = leads[leadIndex];
        // Every event that reaches here is inbound: `Sockets.hook.ts` returns
        // early on `from_me` before its single `leadMessageEvent.emit`, so the
        // message is unread by definition and the old `event.from_me` ternary
        // was dead. Setting `read: false` without also raising `unreadCount`
        // was the BUG-807 half-update — the card's badge is gated on
        // `unreadCount && !read`, so a lead cached at 0 restyled but showed no
        // badge. Increment rather than assign, so a lead that already has N
        // unread bumps to N+1 instead of being pinned.
        //
        // Deliberately NOT idempotent, which is worth explaining because
        // websocket-server's connection state recovery (BUG-807 server half)
        // made delivery at-least-once inside its 20s window, so a replayed
        // event double-increments here.
        //
        // A floor (`Math.max(1, cached)`) would be replay-safe but is the worse
        // trade: `lead_event.updated` travels the same pipeline and the same
        // recovery window, so a replayed fallback is corrected by the replayed
        // authoritative event in the same ~94% of cases as the original. The
        // double-increment therefore persists only when a replayed fallback
        // arrives with no replayed authoritative event, and it self-heals on
        // the next authoritative event or on read. A floor, by contrast, fails
        // on *every* unpaired inbound to an already-unread lead — it would not
        // bump at all, a milder reprise of the very symptom this fixes, for
        // exactly the leads that already show a badge.
        //
        // The durable fix is a contract change, not client logic:
        // `tLeadMessageEvent` carries no message id, so client-side dedupe has
        // no key. Adding one on the websocket side would kill the error class
        // outright — see the BUG-807 follow-ups.
        const updatedLead = {
          ...lead,
          chat: lead.chat
            ? {
                ...lead.chat,
                lastMessageOn: event.message_on,
                lastMessageText: event.message,
                lastMessageFromMe: event.from_me,
                read: false,
                unreadCount: (lead.chat.unreadCount ?? 0) + 1,
              }
            : lead.chat,
        };

        // A lead already at the top still needs the chat update — only the
        // reordering is redundant. Returning the stage untouched here is what
        // left the top card's badge unrendered.
        if (leadIndex === 0) {
          return {
            ...stage,
            leads: [updatedLead, ...leads.slice(1)],
          };
        }

        const remaining = leads.filter((_, i) => i !== leadIndex);
        return {
          ...stage,
          leads: [updatedLead, ...remaining],
        };
      });

      if (!foundInStage) return prevFunnel;

      return {
        ...prevFunnel,
        stages: newStages,
      };
    });
  });

  useEffect(() => {
    fetchWorkspaceFunnels();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [currentWorkspaceId, idToken, addAlert]);

  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Debounce the leads loading to prevent excessive calls
    const timeout = setTimeout(async () => {
      // Ensure currentFunnel is fully loaded and has stages before proceeding
      if (
        !currentFunnel ||
        !currentFunnel.stages ||
        currentFunnel.stages.length === 0
      )
        return;
      // RET-1739: no half-open guard here. `buildDateFilterParams` resolves a
      // one-sided range to a single day, so the request is always well-formed
      // and page 1 must fire. The old guard silently skipped the fetch, which
      // left the board rendering stage totals with zero lead cards.

      // Serialize behind any in-flight batch rather than dropping this one.
      // RET-1739: a range picker fires onChange twice (`[start, null]` then
      // `[start, end]`). Now that the effect also keys on the start date, the
      // first fire really fetches and holds this lock, so `return`ing here
      // dropped the second — and nothing re-triggers it, leaving the board
      // pinned to the single-day result while the picker showed the full
      // range. Awaiting without returning lets the completed range win.
      if (loadingLeadsPromise) {
        await loadingLeadsPromise;
      }

      setLoadingFilteredLeads(true);

      // Captured up front so the settle marker below cannot attribute this
      // batch's counts to a funnel the user switched to while it was in flight.
      const loadedFunnelId = currentFunnel.id;
      const fetchGeneration = ++leadsFetchGenerationRef.current;

      const loadPromise = (async () => {
        try {
          // Use Promise.allSettled to prevent one failed request from affecting others
          const results = await Promise.allSettled(
            currentFunnel.stages.map(
              async (stage) => await getMoreLeads(stage.id, 1),
            ),
          );

          // Log any failed requests for debugging
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              LLog.error(
                {
                  component: LOG_COMPONENTS.UI,
                  method: 'Pipe.hook.tsx',
                  operation: LOG_OPERATIONS.ERROR,
                },
                `Failed to load leads for stage ${currentFunnel.stages[index].id}`,
                result.reason as unknown as Error,
              );
            }
          });
        } catch (error) {
          LLog.error(
            {
              component: LOG_COMPONENTS.UI,
              method: 'Pipe.hook.tsx',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Error loading filtered leads',
            error as unknown as Error,
          );
        } finally {
          setLoadingLeadsPromise(null);

          // A newer batch superseded this one; let it own the loading state
          // and the settle marker.
          if (leadsFetchGenerationRef.current === fetchGeneration) {
            setLoadingFilteredLeads(false);
            // Marked on settle, not on success: a stage whose page failed keeps
            // its zero on the board, so the counts consumers read are the ones
            // the user is actually looking at.
            setLeadCountsLoadedFunnelId(loadedFunnelId);
          }
        }
      })();

      setLoadingLeadsPromise(loadPromise);
      await loadPromise;
    }, 300); // 300ms debounce

    setDebounceTimeout(timeout);

    // Cleanup function
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [
    messageReadFilter,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
    dateRange?.[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
    dateRange?.[1],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
    dateRange?.[0] || dateRange?.[1] ? dateFilterType : undefined,
    integrationFilter,
    statusFilter,
    tagsFilter,
    assignedUserFilters,
    isUndeliveredFilterOn,
    currentFunnel?.id,
    triggerDirectoryReload,
  ]);

  useEffect(() => {
    if (selectedLeadForChatModal) {
      setChatModalIsOpen(true);
    }
  }, [selectedLeadForChatModal, setChatModalIsOpen]);

  // Fetches SmartLeadBot bulk state for the given leads and seeds the chip state (engagement
  // map, escalated set, chat map, per-lead aiRespondEnabled). Extracted from getSelectedFunnelInfo
  // so the flag-flip recovery effect below can reuse it. setCurrentFunnel is functional so it
  // preserves any lazy-loaded lead pages.
  const fetchSlbBulkState = useCallback(
    (leadIds: string[]) => {
      if (!isSmartLeadBotEnabled || !currentWorkspaceId || leadIds.length === 0) return;
      autoRespondService
        .getBulkState(currentWorkspaceId, leadIds)
        .then(({ states }) => {
          const stateMap = new Map(states.map((s) => [s.leadId, { enabled: s.enabled, chatId: s.chatId, channelType: s.channelType, needsAttention: s.needsAttention, engagementState: s.engagementState, pausedReason: s.pausedReason }]));
          setSlbEngagementByLead((prev) => mergeEngagementByLead(prev, states));
          const newMap = new Map<string, { chatId: string; channelType: string }>();
          for (const [leadId, state] of stateMap) {
            if (state.enabled && state.chatId && state.channelType) {
              newMap.set(leadId, { chatId: state.chatId, channelType: state.channelType });
            }
          }
          if (newMap.size > 0) setSlbLeadChatMap(newMap);
          const escalated = new Set<string>();
          for (const [leadId, state] of stateMap) {
            if (state.needsAttention && !wasLeadRecentlyCleared(leadId)) escalated.add(leadId);
          }
          if (escalated.size > 0) {
            setSlbEscalatedLeadIds((prev) => {
              if ([...escalated].every((id) => prev.has(id))) return prev;
              const merged = new Set(prev);
              for (const id of escalated) merged.add(id);
              return merged;
            });
          }
          setCurrentFunnel((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              stages: prev.stages.map((stage) => ({
                ...stage,
                leads: (stage.leads ?? []).map((lead) => {
                  const state = stateMap.get(lead.id);
                  if (!state) return lead;
                  return { ...lead, chat: lead.chat ? { ...lead.chat, aiRespondEnabled: state.enabled } : null };
                }),
              })),
            };
          });
        })
        .catch(() => {/* non-critical */});
    },
    [isSmartLeadBotEnabled, currentWorkspaceId, wasLeadRecentlyCleared],
  );

  // RET-1182 cold-load seeding: this single, order-independent effect is the ONLY
  // trigger that seeds SLB engagement state for the funnel. It fires when the agent
  // is enabled AND a funnel with leads is loaded AND we haven't seeded this funnel
  // yet — no matter whether the feature-flags fetch resolves before or after the
  // funnel loads. (The old approach seeded inline in getSelectedFunnelInfo plus a
  // flag-flip recovery effect; the flip was consumed when the flag resolved before
  // the funnel existed, so chips stayed empty until a manual reload.)
  const seededSlbFunnelIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentFunnel) return;
    const leadIds = currentFunnel.stages
      .flatMap((stage) => stage.leads || [])
      .map((lead) => lead.id)
      .filter((id): id is string => Boolean(id));
    if (
      !shouldSeedSlbState({
        alreadySeededFunnelId: seededSlbFunnelIdRef.current,
        isSmartLeadBotEnabled,
        currentFunnelId: currentFunnel.id,
        leadCount: leadIds.length,
      })
    ) {
      return;
    }
    seededSlbFunnelIdRef.current = currentFunnel.id;
    fetchSlbBulkState(leadIds);
  }, [isSmartLeadBotEnabled, currentFunnel, fetchSlbBulkState]);

  const getSelectedFunnelInfo = useCallback(async (funnel: tWorkspaceFunnel) => {
    try {
      if (!currentWorkspaceId) return;

      setLoading(true);

      const funnelInfoResponse = await PipeService.getFunnelInfo(
        currentWorkspaceId,
        funnel.id,
      );

      // Ensure all stages have leads initialized as arrays, not null
      const funnelWithInitializedLeads = {
        ...funnelInfoResponse.data,
        stages: funnelInfoResponse.data.stages.map((stage) => ({
          ...stage,
          leads: stage.leads || [],
        })),
      };

      // SLB engagement state is seeded by the order-independent effect above
      // (keyed on funnel id), which fires once this setCurrentFunnel commits and
      // the agent flag is enabled — so seeding no longer races the feature-flags
      // fetch. No inline seed here.
      setCurrentFunnel(funnelWithInitializedLeads);
      setCurrentFunnelID(funnelInfoResponse.data.id);
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer la información del embudo seleccionado',
      });
    } finally {
      setLoading(false);
    }
  }, [addAlert, currentWorkspaceId, setCurrentFunnel, setCurrentFunnelID, setLoading]);

  const onDragEnd = useCallback(async (result: DropResult) => {
    try {
      const { draggableId, source, destination, type } = result;

      if (
        !destination ||
        !currentFunnel ||
        !currentWorkspaceId ||
        !currentUserId
      )
        return;

      if (type === 'stage') {
        const newStages = currentFunnel.stages;
        const [movedStage] = newStages.splice(source.index, 1);

        if (movedStage.name === INBOX_STAGE_NAME || movedStage.isVirtual) return;

        newStages.splice(destination.index, 0, movedStage);

        setCurrentFunnel((prevFunnel) => {
          if (!prevFunnel) return null;

          const inbox = newStages.find((stage) => stage.name === INBOX_STAGE_NAME);
          const unclassified = newStages.find((stage) => stage.isVirtual);

          if (newStages[0].name !== INBOX_STAGE_NAME && inbox) {
            const filtered = newStages.filter(
              (stage) => stage.name !== INBOX_STAGE_NAME && !stage.isVirtual,
            );
            const prefix = unclassified ? [unclassified, inbox] : [inbox];
            return {
              ...prevFunnel,
              stages: [...prefix, ...filtered],
            };
          }

          if (unclassified && newStages[0]?.id !== unclassified.id) {
            return {
              ...prevFunnel,
              stages: [
                unclassified,
                ...newStages.filter((stage) => !stage.isVirtual),
              ],
            };
          }

          return {
            ...prevFunnel,
            stages: newStages,
          };
        });

        const newValues = {
          stages: newStages
            .filter((stage) => stage.name !== INBOX_STAGE_NAME && !stage.isVirtual)
            .map((stage) => funnelStageLegacyParser(stage)),
        };

        const response = await PipeService.updateFunnel(
          currentWorkspaceId,
          currentFunnel?.id,
          currentUserId,
          newValues,
        );

        if (!response || !response.data) {
          throw new Error('Ocurrió un error al actualizar tu embudo.');
        }

        // Centro de Retos (AGI-498): a saved change to a stage is what
        // completes "ajusta las etapas de tu embudo". Opening the editor is not.
        signalChallenge('review_stages');

        addAlert({
          type: eAlertType.SUCCESS,
          message: 'Tu embudo se actualizó con éxito.',
        });

        return;
      }

      if (destination.droppableId === UNCLASSIFIED_UUID) return;

      const { stages } = currentFunnel;

      const sourceStage = stages.find(
        (stage) => stage.id === source.droppableId,
      );

      if (source.droppableId === destination.droppableId || !sourceStage) {
        return;
      }

      const draggableLead = sourceStage.leads?.find(
        (lead) => lead.id === draggableId,
      );
      const newStages = stages.map((stage) => {
        if (stage.id === source.droppableId) {
          return {
            ...stage,
            leads: stage.leads?.filter((lead) => lead.id !== draggableId) || [],
          };
        }

        if (stage.id === destination.droppableId && draggableLead) {
          const stageLeads = stage.leads || [];

          return {
            ...stage,
            leads: [draggableLead, ...stageLeads],
          };
        }
        return stage;
      });
      setCurrentFunnel((prevValue) => {
        if (prevValue) {
          return {
            ...prevValue,
            stages: newStages,
          };
        }
        return prevValue;
      });

      const newValue =
        destination.droppableId === NIL_UUID
          ? { in_inbox: true }
          : {
              stage_uuid: destination.droppableId,
              funnel_uuid: currentFunnel.id,
              in_inbox: false,
            };

      const response = await PipeService.updateLead(
        currentWorkspaceId,
        draggableId,
        currentUserId,
        newValue,
      );

      if (!response || !response.data) {
        throw new Error('Ocurrió un error al actualizar tu lead.');
      }

      // LEA-50 (U16): the seller's own move into a stage mapped to a result
      // opens its form, and the availability prompt waits until it closes.
      const prompted = leadClose.enabled ? promptOf(response) : null;
      if (prompted && draggableLead) {
        leadClose.openForm({
          lead: toCloseLeadRef({
            id: draggableLead.id,
            name: draggableLead.name,
            stage: { name: stages.find((stage) => stage.id === destination.droppableId)?.name },
            value: draggableLead.value,
          }),
          type: prompted,
          source: 'stage_entry',
          surface: 'board',
          deferAvailability: true,
        });
      } else {
        // Deliberately below the `type === 'stage'` early return above: that
        // branch reorders the board's COLUMNS, which is configuration work and
        // says nothing about whether the seller is working leads. Only a real
        // lead move contradicts being Inactivo (RET-1350).
        requestAvailabilityPrompt(eAvailabilityPromptReason.LEAD_MOVED);
      }

      // "Lead moved past entry" is the starkest retained-vs-churned separator
      // (05-29 lever analysis) and this board is its main surface — it had no
      // event. Chat-side moves are covered by front.chat.estatus.select.
      trackEvent('front.funnels.lead.stage_moved', {
        leadId: draggableId,
        funnel_id: currentFunnel.id,
        from_stage_id: source.droppableId,
        to_stage_id: destination.droppableId,
        to_inbox: destination.droppableId === NIL_UUID,
      });
      window.dispatchEvent(
        new CustomEvent(LEAD_MOVED_EVENT, {
          detail: { leadId: draggableId, toStageId: destination.droppableId },
        }),
      );

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Tu lead se actualizó con éxito.',
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al actualizar. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, leadClose, setCurrentFunnel, signalChallenge, tFunnel, trackEvent]);

  const addStageInIndex = useCallback(async (index: number, config?: tCreateStageConfig) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return;

      const newStages = currentFunnel.stages;
      const newStageUuid = uuid();
      const newStage: tFunnelStage = {
        id: newStageUuid,
        name: config?.name ?? `Etapa ${index + 1}`,
        expiresAfter: 0,
        leadCount: 0,
        order: 0,
        sumValue: '',
        leads: [],
        description: config?.description,
        botEnabled: config?.botEnabled,
      };
      newStages.splice(index + 1, 0, newStage);

      const newValues = {
        stages: newStages
          .filter((stage) => stage.name !== INBOX_STAGE_NAME && !stage.isVirtual)
          .map((stage) => funnelStageLegacyParser(stage)),
      };

      const response = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        newValues,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió información al crear tu nueva etapa.');
      }

      // Centro de Retos (AGI-498): a saved change to a stage is what
      // completes "ajusta las etapas de tu embudo". Opening the editor is not.
      signalChallenge('review_stages');

      setCurrentFunnel((prevValue) => {
        if (!prevValue) return null;

        const prevStages = prevValue.stages;
        const prefix = prevStages.filter(
          (stage) => stage.isVirtual || stage.name === INBOX_STAGE_NAME,
        );
        const newStagesFromResponse = response.data.stages
          .filter((stage) => !stage.is_virtual)
          .map((stage) => {
            const parsed = funnelStagesParser(stage);
            const prevStage = prevStages.find((p) => p.id === stage.uuid);
            // Defensive merge: preserves prev sumValue / description / botEnabled
            // if the HTTP response ever ships sparse (BUG-460 class).
            const merged = mergeFunnelStageWithPrev(parsed, prevStage);
            return stage.uuid === newStageUuid
              ? { ...merged, isNew: true, leads: [] }
              : { ...merged, leads: prevStage?.leads ?? [] };
          });

        return {
          ...prevValue,
          stages: [...prefix, ...newStagesFromResponse],
        };
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al crear tu nueva etapa. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, setCurrentFunnel, signalChallenge, tFunnel]);

  const removeStage = useCallback(async (stageToMigrateUuid: string) => {
    try {
      if (
        !currentFunnel ||
        !currentWorkspaceId ||
        !currentUserId ||
        !selectedStage
      )
        return;

      const updateLeadsInBulkPayload = {
        filters: {
          stage_uuid: selectedStage.id,
        },
        set: {
          funnel_uuid: currentFunnel.id,
          stage_uuid: stageToMigrateUuid,
          in_inbox: false,
        },
      };

      const updateLeadsInBulkResponse = await PipeService.updateLeadsInBulk(
        currentWorkspaceId,
        currentUserId,
        updateLeadsInBulkPayload,
      );

      if (!updateLeadsInBulkResponse || !updateLeadsInBulkResponse.data) {
        throw new Error('No se recibió información al mover los leads.');
      }

      const prevStages = currentFunnel.stages;
      const newStages = prevStages
        .filter((stage) => stage.id !== selectedStage?.id)
        .map((stage) => {
          if (stage.id === stageToMigrateUuid) {
            const newLeads = selectedStage.leads || [];
            const prevLeads = stage.leads || [];

            return {
              ...stage,
              leads: [...newLeads, ...prevLeads],
            };
          }

          return stage;
        });
      const newValues = {
        stages: newStages
          .filter((stage) => stage.name !== INBOX_STAGE_NAME && !stage.isVirtual)
          .map((stage) => funnelStageLegacyParser(stage)),
      };

      const updateFunnelResponse = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        newValues,
      );

      if (!updateFunnelResponse || !updateFunnelResponse.data) {
        throw new Error('No se recibió información al borrar la etapa.');
      }

      // Centro de Retos (AGI-498): a saved change to a stage is what
      // completes "ajusta las etapas de tu embudo". Opening the editor is not.
      signalChallenge('review_stages');

      setCurrentFunnel((prevFunnel) => {
        if (!prevFunnel || !selectedStage) return null;

        const prevStages = prevFunnel.stages;
        const prefix = prevStages.filter(
          (stage) => stage.isVirtual || stage.name === INBOX_STAGE_NAME,
        );
        const newStagesFromResponse = updateFunnelResponse.data.stages
          .filter((stage) => !stage.is_virtual)
          .map((stage) => {
            const parsed = funnelStagesParser(stage);
            const prevStage = prevStages.find((p) => p.id === stage.uuid);
            // Defensive merge: preserves prev sumValue / description / botEnabled
            // if the HTTP response ever ships sparse (BUG-460 class).
            const merged = mergeFunnelStageWithPrev(parsed, prevStage);
            return { ...merged, leads: prevStage?.leads ?? [] };
          });

        return {
          ...prevFunnel,
          stages: [...prefix, ...newStagesFromResponse],
        };
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Se ha eliminado de manera correcta tu etapa',
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al eliminar la etapa. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, selectedStage, setCurrentFunnel, signalChallenge, tFunnel]);

  const updateFunnelName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newName = e.currentTarget.value;

    if (typeof newName !== 'string') return;

    setCurrentFunnel((prevValue) => {
      if (!prevValue) return null;
      return {
        ...prevValue,
        name: newName,
      };
    });
  }, [setCurrentFunnel]);

  const updateFunnelDescription = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const newDescription = e.currentTarget.value;

    if (typeof newDescription !== 'string') return;

    setCurrentFunnel((prevValue) => {
      if (!prevValue) return null;
      return {
        ...prevValue,
        description: newDescription,
      };
    });
  }, [setCurrentFunnel]);

  const updateFunnelEmoji = useCallback(async (emoji: EmojiClickData) => {
    const prevEmoji = currentFunnel?.emoji || '';
    try {
      if (!currentWorkspaceId || !currentFunnel || !currentUserId) return;
      const newValues = {
        emoji: emoji.emoji,
      };

      setCurrentFunnel((prevValue) => {
        if (prevValue) {
          return {
            ...prevValue,
            ...newValues,
          };
        }
        return prevValue;
      });

      const response = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        newValues,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió data del nuevo embudo creado');
      }
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Tu embudo se actualizó con éxito.',
      });
    } catch (err) {
      setCurrentFunnel((prevValue) => {
        if (prevValue) {
          return {
            ...prevValue,
            emoji: prevEmoji,
          };
        }
        return prevValue;
      });
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al actualizar tu embudo. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, setCurrentFunnel, tFunnel]);

  const toggleEditMode = useCallback((value: boolean) => {
    setEditMode(value);
  }, [setEditMode]);

  const updateFunnelOnEditMode = useCallback(async (newValues: tEditFunnelFields) => {
    try {
      if (!currentWorkspaceId || !currentFunnel || !currentUserId) return;

      // Filter currentFunnel.users to only SELLER users (same as what's in newValues.users)
      // This ensures we only compare SELLER users with SELLER users, preventing OWNER/ADMIN from being deleted
      const currentSellerUsers = (currentFunnel.users || []).filter(
        (u) => u.role === eUserRole.SELLER,
      );

      // Find the current user in the funnel to prevent self-deletion
      // Check both SELLER users and ALL users (in case current user is ADMIN/OWNER)
      const currentUserInFunnel = currentSellerUsers.find(
        (u) => u.authID === currentUserId,
      );
      // Also check in all users (not just SELLER) to handle edge cases
      const currentUserInAllUsers = (currentFunnel.users || []).find(
        (u) => u.authID === currentUserId,
      );

      // Calculate which SELLER users were deleted (in currentSellerUsers but not in newValues.users)
      const currentUserIds = new Set(currentSellerUsers.map((u) => u.id));
      const newUserIds = new Set(newValues.users.map((u) => u.id));
      const deletedUserIds = Array.from(currentUserIds).filter(
        (id) => !newUserIds.has(id),
      );

      // Get deleted users (for remove_users request) - only SELLER users
      // IMPORTANT: Exclude the current user from deletion to prevent them from losing access
      const deletedUsers =
        deletedUserIds.length > 0
          ? currentSellerUsers
              .filter(
                (u) =>
                  deletedUserIds.includes(u.id) && u.authID !== currentUserId, // Prevent deleting the current user
              )
              .map((user) => funnelUserLegacyParser(user))
          : [];

      // If the current user was removed from selection, add them back to ensure they maintain access
      // This prevents the "no access" error after deletion
      const usersToAddOrUpdateBase = newValues.users.map((user) =>
        funnelUserLegacyParser(user),
      );

      // CRITICAL: Always ensure current user (if they're a SELLER and were in the funnel) is added back
      // This handles the case when all users are deleted
      const usersToAddOrUpdate = (() => {
        const users = [...usersToAddOrUpdateBase];

        if (currentUserInFunnel) {
          const isCurrentUserInNewValues = newValues.users.find(
            (u) => u.authID === currentUserInFunnel.authID,
          );
          if (!isCurrentUserInNewValues) {
            // Current user was removed from selection, add them back
            const currentUserForUpdate = funnelUserLegacyParser({
              authID: currentUserInFunnel.authID,
              id: currentUserInFunnel.id,
              name: currentUserInFunnel.name,
              role: currentUserInFunnel.role,
              autoAssign: currentUserInFunnel.autoAssign,
              profilePicture: currentUserInFunnel.profilePicture,
            });
            users.push(currentUserForUpdate);
          }
        } else if (
          currentUserInAllUsers &&
          currentUserInAllUsers.role === eUserRole.SELLER &&
          !newValues.users.find(
            (u) => u.authID === currentUserInAllUsers.authID,
          )
        ) {
          // Fallback: If current user is a SELLER but wasn't found in currentSellerUsers,
          // add them back using currentUserInAllUsers data
          const currentUserForUpdate = funnelUserLegacyParser({
            authID: currentUserInAllUsers.authID,
            id: currentUserInAllUsers.id,
            name: currentUserInAllUsers.name,
            role: currentUserInAllUsers.role,
            autoAssign: currentUserInAllUsers.autoAssign,
            profilePicture: currentUserInAllUsers.profilePicture,
          });
          users.push(currentUserForUpdate);
        }

        return users;
      })();

      const newValuesForEndpoint: Partial<tFunnelLegacy> = {
        name: newValues.name,
        emoji: newValues.emoji,
        description: newValues.description,
        is_default: newValues.isDefault,
        is_public: newValues.isPublic,
        can_view_handle: newValues.canViewHandle === 'totalAccess',
      };

      // If there are deleted users, send remove request first
      if (deletedUsers.length > 0) {
        const removeRequest = {
          ...newValuesForEndpoint,
          remove_users: true,
          users: deletedUsers.map((user) => ({
            uuid: user.uuid,
            autoassignable: user.autoassignable,
          })),
        } as tUpdateFunnelRequest;

        await PipeService.updateFunnel(
          currentWorkspaceId,
          currentFunnel?.id,
          currentUserId,
          removeRequest,
        );
      }

      // CRITICAL: Final safeguard - ensure current user is always in usersToAddOrUpdate
      // This handles edge cases where the current user might have been missed
      if (
        currentUserInFunnel &&
        !usersToAddOrUpdate.find(
          (u) => u.auth_id === currentUserInFunnel.authID,
        )
      ) {
        const currentUserForUpdate = funnelUserLegacyParser({
          authID: currentUserInFunnel.authID,
          id: currentUserInFunnel.id,
          name: currentUserInFunnel.name,
          role: currentUserInFunnel.role,
          autoAssign: currentUserInFunnel.autoAssign,
          profilePicture: currentUserInFunnel.profilePicture,
        });
        usersToAddOrUpdate.push(currentUserForUpdate);
      }

      // Send add/update request for remaining users
      // CRITICAL: Always send a request if we have users to add/update OR if we deleted users
      // This ensures the current user is always added back even if all other users were deleted
      if (usersToAddOrUpdate.length > 0) {
        const addUpdateRequest = {
          ...newValuesForEndpoint,
          remove_users: false,
          users: usersToAddOrUpdate.map((user) => ({
            uuid: user.uuid,
            autoassignable: user.autoassignable,
          })),
        } as tUpdateFunnelRequest;

        const response = await PipeService.updateFunnel(
          currentWorkspaceId,
          currentFunnel?.id,
          currentUserId,
          addUpdateRequest,
        );

        if (!response || !response.data) {
          throw new Error('No se recibió información al actualizar tu embudo.');
        }

        // Update currentFunnel with the response data
        // IMPORTANT: Preserve existing leads and workspaceUsers from currentFunnel
        // The API response from updateFunnel doesn't include workspaceUsers, so we must preserve it
        const updatedFunnel = funnelParser(response.data);

        // CRITICAL: Ensure current user is always in the users array after update
        // This prevents API failures when the backend response doesn't include the current user
        const usersWithCurrentUser = (() => {
          if (!currentUserInFunnel) return updatedFunnel.users;

          const currentUserInUpdatedFunnel = updatedFunnel.users.find(
            (u) => u.authID === currentUserInFunnel.authID,
          );
          if (currentUserInUpdatedFunnel) return updatedFunnel.users;

          // Current user is missing from API response, add them back
          const currentUserForFunnel = {
            authID: currentUserInFunnel.authID,
            id: currentUserInFunnel.id,
            name: currentUserInFunnel.name,
            role: currentUserInFunnel.role,
            autoAssign: currentUserInFunnel.autoAssign,
            profilePicture: currentUserInFunnel.profilePicture,
          };
          return [...updatedFunnel.users, currentUserForFunnel];
        })();

        const funnelWithPreservedData = {
          ...updatedFunnel,
          users: usersWithCurrentUser, // Use users array with current user guaranteed
          // Preserve workspaceUsers from currentFunnel (API response doesn't include it)
          workspaceUsers:
            currentFunnel?.workspaceUsers || updatedFunnel.workspaceUsers,
          stages: updatedFunnel.stages.map((updatedStage) => {
            // Find the corresponding stage in currentFunnel to preserve its leads
            const existingStage = currentFunnel?.stages.find(
              (s) => s.id === updatedStage.id,
            );
            return {
              ...updatedStage,
              leads: existingStage?.leads || updatedStage.leads || [],
              ...pickStagePagination(updatedStage, existingStage),
            };
          }),
        };
        setCurrentFunnel(funnelWithPreservedData);
      } else if (deletedUsers.length > 0) {
        // If we only deleted users, refresh the funnel to get updated state
        // IMPORTANT: Preserve existing leads and workspaceUsers from currentFunnel
        // getFunnelInfo should include workspaceUsers, but we preserve it as a safety measure
        const funnelInfoResponse = await PipeService.getFunnelInfo(
          currentWorkspaceId,
          currentFunnel.id,
        );
        if (funnelInfoResponse?.data) {
          // getFunnelInfo returns tFunnel (already parsed), not tFunnelLegacy
          const updatedFunnel = funnelInfoResponse.data;

          // CRITICAL: Ensure current user is always in the users array after refresh
          // This prevents API failures when the backend response doesn't include the current user
          const usersWithCurrentUser = (() => {
            if (!currentUserInFunnel) return updatedFunnel.users;

            const currentUserInUpdatedFunnel = updatedFunnel.users.find(
              (u) => u.authID === currentUserInFunnel.authID,
            );
            if (currentUserInUpdatedFunnel) return updatedFunnel.users;

            // Current user is missing from API response, add them back
            const currentUserForFunnel = {
              authID: currentUserInFunnel.authID,
              id: currentUserInFunnel.id,
              name: currentUserInFunnel.name,
              role: currentUserInFunnel.role,
              autoAssign: currentUserInFunnel.autoAssign,
              profilePicture: currentUserInFunnel.profilePicture,
            };
            return [...updatedFunnel.users, currentUserForFunnel];
          })();

          const funnelWithPreservedData = {
            ...updatedFunnel,
            users: usersWithCurrentUser, // Use users array with current user guaranteed
            // Preserve workspaceUsers from currentFunnel if API response doesn't have it
            workspaceUsers:
              updatedFunnel.workspaceUsers || currentFunnel?.workspaceUsers,
            stages: updatedFunnel.stages.map((updatedStage) => {
              // Find the corresponding stage in currentFunnel to preserve its leads
              const existingStage = currentFunnel?.stages.find(
                (s) => s.id === updatedStage.id,
              );
              return {
                ...updatedStage,
                leads: existingStage?.leads || updatedStage.leads || [],
                ...pickStagePagination(updatedStage, existingStage),
              };
            }),
          };
          setCurrentFunnel(funnelWithPreservedData);
        }
      } else {
        // No user changes, just update other fields
        const response = await PipeService.updateFunnel(
          currentWorkspaceId,
          currentFunnel?.id,
          currentUserId,
          newValuesForEndpoint,
        );

        if (!response || !response.data) {
          throw new Error('No se recibió información al actualizar tu embudo.');
        }

        setCurrentFunnel((prevFunnel) => {
          if (!prevFunnel) return null;

          return {
            ...prevFunnel,
            ...newValues,
            canViewHandle: newValues.canViewHandle === 'totalAccess',
          };
        });
      }

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Tu embudo se actualizó con éxito.',
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al actualizar tu embudo. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, setCurrentFunnel, tFunnel]);

  const updateAutoAssignUserStatus = useCallback(async (authId: string) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return;

      let users: tFunnelUser[] = [];
      if (currentFunnel && Array.isArray(currentFunnel.users)) {
        users = currentFunnel.users;
      }

      const newUsers = users.map((user) => {
        if (user.authID !== authId) return user;

        return {
          ...user,
          autoAssign: !user.autoAssign,
        };
      });

      const newValues = {
        users: newUsers.map((user) => funnelUserLegacyParser(user)),
      };

      const response = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        newValues,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió información al actualizar tu usuario.');
      }

      setCurrentFunnel((prevValue) => {
        if (!prevValue) return null;

        return {
          ...prevValue,
          users: newUsers,
        };
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Tu embudo se actualizó con éxito.',
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al actualizar los usuarios de tu embudo. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, setCurrentFunnel, tFunnel]);

  /**
   * Saves one stage's stagnant threshold, in minutes.
   *
   * Returns a result instead of raising a toast: the caller is a modal that is
   * still on screen, and the server now rejects a non-owner/admin with a 403
   * (U2/U7). Surfacing that as `addAlert({ message: (err as Error).message })`
   * put "Request failed with status code 403" in front of the customer, which
   * is both untranslated and unactionable — the modal renders a sentence about
   * permissions instead, and keeps itself open so nothing is lost.
   */
  const updateExpiresAfterValue = useCallback(async (stageToUpdateUuid: string, newValue: number): Promise<tUpdateExpiresAfterResult> => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) {
        return { ok: false };
      }

      const newStages = currentFunnel.stages.map((stage) => {
        if (stage.id !== stageToUpdateUuid) return stage;

        return {
          ...stage,
          expiresAfter: newValue,
        };
      });

      const newValues = {
        stages: newStages
          .filter((stage) => stage.name !== INBOX_STAGE_NAME && !stage.isVirtual)
          .map((stage) => funnelStageLegacyParser(stage)),
      };

      const response = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        newValues,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió información actualizar tu etapa.');
      }

      // Centro de Retos (AGI-498): a saved change to a stage is what
      // completes "ajusta las etapas de tu embudo". Opening the editor is not.
      signalChallenge('review_stages');

      isBackgroundResyncRef.current = false;
      setFunnelRefreshToken((prev) => prev + 1);

      return { ok: true };
    } catch (err) {
      return { ok: false, status: httpStatusOf(err) };
    }
  }, [currentFunnel, currentUserId, currentWorkspaceId, isBackgroundResyncRef, setFunnelRefreshToken, signalChallenge]);

  const updateStageConfiguration = useCallback(async (stageToUpdateId: string, description: string, botEnabled: boolean): Promise<void> => {
    if (!currentFunnel || !currentWorkspaceId || !currentUserId) return;

    const previousFunnel = currentFunnel;
    const updatedStages = currentFunnel.stages.map((stage) =>
      stage.id === stageToUpdateId
        ? { ...stage, description, botEnabled }
        : stage,
    );

    setCurrentFunnel((prev) => {
      if (!prev) return null;
      return { ...prev, stages: updatedStages };
    });

    try {
      const legacyStages = updatedStages
        .filter((stage) => stage.name !== INBOX_STAGE_NAME && !stage.isVirtual)
        .map(funnelStageLegacyParser);

      const response = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel.id,
        currentUserId,
        { stages: legacyStages },
      );

      if (!response || !response.data) {
        throw new Error('No se recibió información al actualizar la etapa.');
      }

      // Centro de Retos (AGI-498): a saved change to a stage is what
      // completes "ajusta las etapas de tu embudo". Opening the editor is not.
      signalChallenge('review_stages');

      // Cascade bot_enabled change to chatbot-service so it activates/deactivates
      // SLB sessions for leads in this stage. The legacy funnel PUT only updates
      // leads-service — chatbot-service needs the modern endpoint to know about it.
      if (isSmartLeadBotEnabled && slbHasActiveIntegration) {
        SmartLeadbotService
          .saveBotEnabled(currentWorkspaceId, currentFunnel.id, [{ stageId: stageToUpdateId, botEnabled }])
          .catch(() => {
            LLog.warn(
              { component: LOG_COMPONENTS.API, method: 'updateStageConfiguration', operation: LOG_OPERATIONS.ERROR },
              'Failed to cascade bot_enabled to chatbot-service',
            );
          });
      }
    } catch {
      setCurrentFunnel(previousFunnel);
      addAlert({
        type: eAlertType.ERROR,
        message: tFunnel('stage.stageDescriptionModal.errorSave'),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, isSmartLeadBotEnabled, setCurrentFunnel, signalChallenge, slbHasActiveIntegration, tFunnel]);

  const moveLeadToStage = useCallback((leadId: string, toStageId: string): void => {
    setCurrentFunnel((prev) => {
      if (!prev) return null;
      let leadToMove: tFunnelLead | undefined;
      const source = prev.stages.find(stage => stage.leads?.some(lead => lead.id === leadId));
      if (!source || source.id === toStageId) return prev;
      const stagesWithoutLead = prev.stages.map((stage) => {
        const idx = (stage.leads ?? []).findIndex((l) => l.id === leadId);
        if (idx === -1) return stage;
        leadToMove = stage.leads![idx];
        return { ...stage, leadCount: Math.max(0, (stage.leadCount ?? 0) - 1), leads: stage.leads!.filter((l) => l.id !== leadId) };
      });
      if (!leadToMove) return prev;
      return {
        ...prev,
        stages: stagesWithoutLead.map((stage) =>
          stage.id === toStageId
            ? { ...stage, leadCount: (stage.leadCount ?? 0) + 1, leads: [leadToMove!, ...(stage.leads ?? [])] }
            : stage,
        ),
      };
    });
  }, [setCurrentFunnel]);

  const removeLeadFromFunnel = useCallback((leadId: string): void => {
    setCurrentFunnel((prev) => {
      if (!prev) return null;
      if (!prev.stages.some(stage => stage.leads?.some(lead => lead.id === leadId))) return prev;
      return {
        ...prev,
        stages: prev.stages.map((stage) => !stage.leads?.some(lead => lead.id === leadId) ? stage : ({
          ...stage,
          leadCount: stage.leads?.some((l) => l.id === leadId)
            ? Math.max(0, (stage.leadCount ?? 0) - 1)
            : stage.leadCount,
          leads: stage.leads ? stage.leads.filter((l) => l.id !== leadId) : stage.leads,
        })),
      };
    });
  }, [setCurrentFunnel]);

  leadActiveChangedEvent.useSubscription(change => {
    scheduleUndeliveredCountRefresh();
    if (!change.active) {
      removeLeadFromFunnel(change.id);
      return;
    }
    // The active event carries no card data; refill using current server filters.
    isBackgroundResyncRef.current = true;
    setFunnelRefreshToken(prev => prev + 1);
  });

  const updateLeadAssignee = useMemoizedFn((leadId: string, assignedUser: tFunnelLead['assignedUser']): void => {
    if (!matchesAssignedUserFilter(assignedUser?.authID, assignedUserFilters)) {
      removeLeadFromFunnel(leadId);
      return;
    }
    setCurrentFunnel(prev => {
      if (!prev) return prev;
      let changed = false;
      const stages = prev.stages.map(stage => {
        if (!stage.leads?.some(lead => lead.id === leadId)) return stage;
        const leads = stage.leads.map(lead => {
          if (lead.id !== leadId || (lead.assignedUser?.authID === assignedUser?.authID && lead.assignedUser?.name === assignedUser?.name)) return lead;
          changed = true;
          return { ...lead, assignedUser };
        });
        return changed ? { ...stage, leads } : stage;
      });
      return changed ? { ...prev, stages } : prev;
    });
  });

  const onLeadTagsChanged = useMemoizedFn((change: { leadId: string; tag: string; attached: boolean }): void => {
    scheduleUndeliveredCountRefresh();
    if (!change.attached && tagsFilter?.some(filter => filter.name === change.tag)) {
      removeLeadFromFunnel(change.leadId);
    }
    // Current date/delivery membership and card fields belong to the board API.
    isBackgroundResyncRef.current = true;
    setFunnelRefreshToken(prev => prev + 1);
  });

  const updateLeadAiRespondState = useCallback((leadId: string, enabled: boolean): void => {
    setCurrentFunnel((prev) => {
      if (!prev) return null;
      const stageIdx = prev.stages.findIndex((s) => (s.leads ?? []).some((l) => l.id === leadId));
      if (stageIdx === -1) return prev;
      const currentLead = prev.stages[stageIdx].leads?.find((lead) => lead.id === leadId);
      if (!currentLead?.chat || currentLead.chat.aiRespondEnabled === enabled) return prev;
      const updatedStages = prev.stages.map((stage, i) => {
        if (i !== stageIdx) return stage;
        return {
          ...stage,
          leads: (stage.leads ?? []).map((lead) =>
            lead.id === leadId && lead.chat
              ? { ...lead, chat: { ...lead.chat, aiRespondEnabled: enabled } }
              : lead,
          ),
        };
      });
      return { ...prev, stages: updatedStages };
    });
  }, [setCurrentFunnel]);

  // TODO: Restore aiRespondEnabled validation once activation flow is finalized.
  // Original logic: only enable SLB UI when the workspace has aiRespondEnabled === true.
  useEffect(() => {
    if (!isSmartLeadBotEnabled || !currentWorkspaceId) return;
    // All workspaces can activate SLB (no workspace gate for testing).
    setSlbWorkspaceEnabled(true);
    // SMART tags and PENDIENTE badges only show when aiRespondEnabled is true
    // (which means at least one integration is active in the SLB config).
    smartLeadbotConfigService
      .getConfig(currentWorkspaceId)
      .then((cfg) => {
        setSlbHasActiveIntegration(cfg.aiRespondEnabled ?? false);
        setSlbConfiguredFunnelId(cfg.funnelId ?? null);
        setSlbActiveFunnelIds(
          cfg.funnelIds && cfg.funnelIds.length > 0
            ? cfg.funnelIds
            : cfg.funnelId
              ? [cfg.funnelId]
              : [],
        );
      })
      .catch(() => {
        setSlbHasActiveIntegration(false);
        setSlbConfiguredFunnelId(null);
        setSlbActiveFunnelIds([]);
      });
  }, [currentWorkspaceId, isSmartLeadBotEnabled]);

  // Listen for SLB config changes from the config page (same or other tab).
  // When SLB is deactivated at the workspace level, clear all sparkles and SMART badges.
  useEffect(() => {
    if (!isSmartLeadBotEnabled) return;
    return onSlbConfigChange((msg) => {
      if (msg.workspaceId !== currentWorkspaceId) return;
      setSlbHasActiveIntegration(msg.aiRespondEnabled);
      if (msg.funnelId !== undefined) setSlbConfiguredFunnelId(msg.funnelId);
      // AGI-16 (F9): update the active set from the broadcast so the badge tracks
      // every active funnel live on toggle. Prefer the full funnelIds; fall back
      // to the singular funnelId for older senders that don't carry the set.
      if (msg.funnelIds) setSlbActiveFunnelIds(msg.funnelIds);
      else if (msg.funnelId !== undefined) setSlbActiveFunnelIds(msg.funnelId ? [msg.funnelId] : []);
      if (!msg.aiRespondEnabled) {
        // Clear all sparkles — sessions were bulk-deactivated by the backend
        setSlbEscalatedLeadIds(new Set());
        clearSlbChatMap();
      }
    });
  }, [currentWorkspaceId, isSmartLeadBotEnabled]);

  const updateStageName = useCallback(async (stageToUpdateUuid: string, newValue: string) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return;

      const newStages = currentFunnel.stages.map((stage) => {
        if (stage.id !== stageToUpdateUuid) return stage;

        return {
          ...stage,
          name: newValue,
          isNew: false,
        };
      });

      setCurrentFunnel((prevValue) => {
        if (!prevValue) return null;

        return {
          ...prevValue,
          stages: newStages,
        };
      });

      const newValues = {
        stages: newStages
          .filter((stage) => stage.name !== INBOX_STAGE_NAME && !stage.isVirtual)
          .map((stage) => funnelStageLegacyParser(stage)),
      };

      const response = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        newValues,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió información al actualizar la etapa.');
      }

      // Centro de Retos (AGI-498): a saved change to a stage is what
      // completes "ajusta las etapas de tu embudo". Opening the editor is not.
      signalChallenge('review_stages');
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al actualizar la etapa. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, setCurrentFunnel, signalChallenge, tFunnel]);

  const createNewFunnel = useCallback(async (newValues: tCreateFunnelForm) => {
    try {
      if (!currentWorkspaceId || !currentUserId) return;

      const newFunnel = {
        ...newValues,
        stages: initialStagesForCreatingFunnel,
      };

      const response = await PipeService.createFunnel(
        currentWorkspaceId,
        currentUserId,
        newFunnel,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió data del nuevo embudo creado');
      }
      setWorkspaceFunnels((prevFunnels) => {
        const newParsedFunnel = funnelParser(response.data);

        getSelectedFunnelInfo(newParsedFunnel);

        return [...prevFunnels, newParsedFunnel];
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Tu embudo se creó con éxito.',
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al crear tu nuevo embudo. Intenta mas tarde.',
      });
    }
  }, [addAlert, currentUserId, currentWorkspaceId, getSelectedFunnelInfo, setWorkspaceFunnels]);

  const updateFunnel = useCallback(async (newValues: Partial<tFunnelLegacy>) => {
    try {
      if (!currentWorkspaceId || !currentFunnel || !currentUserId) return;

      const response = await PipeService.updateFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        newValues,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió data del nuevo embudo creado');
      }
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Tu embudo se actualizó con éxito.',
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          funnelWriteErrorMessage(
            err,
            tFunnel('funnelWrite.errors.forbidden'),
            'Error al actualizar tu embudo. Intenta mas tarde.',
          ),
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, tFunnel]);

  const removeFunnel = useCallback(async (funnelToMigrateId: string, stageToMigrateId: string) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return;

      const updateLeadsInBulkPayload = {
        filters: {
          funnel_uuid: currentFunnel.id,
        },
        set: {
          funnel_uuid: funnelToMigrateId,
          stage_uuid: stageToMigrateId,
          in_inbox: false,
        },
      };

      await PipeService.deleteFunnel(
        currentWorkspaceId,
        currentFunnel?.id,
        currentUserId,
        updateLeadsInBulkPayload,
      );

      setWorkspaceFunnels((prevFunnels) => {
        const defaultFunnel = prevFunnels.find((funnel) => funnel.isDefault);
        const newFunnels = prevFunnels.filter(
          (funnel) => funnel.id !== currentFunnel?.id,
        );

        if (defaultFunnel) getSelectedFunnelInfo(defaultFunnel);

        return newFunnels;
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Se ha eliminado de manera correcta tu embudo',
      });
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al borrar el embudo. Intenta mas tarde.',
      });
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, getSelectedFunnelInfo, setWorkspaceFunnels]);

  const getFunnelsWithStages = useCallback(async () => {
    if (!currentWorkspaceId || !idToken) return;

    try {
      setLoadingStagesForDeleteFunnelModal(true);

      const response =
        await PipeService.getWorkspaceFunnelsWithStages(currentWorkspaceId);

      setFunnelsWithStages(response.data.pipes);
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer los embudos para el modal',
      });
    } finally {
      setLoadingStagesForDeleteFunnelModal(false);
    }
  }, [addAlert, currentWorkspaceId, idToken, setFunnelsWithStages, setLoadingStagesForDeleteFunnelModal]);

  const processLeadForWhatsappBusiness = useCallback((lead: tFunnelLead) => {
    {
      if (
        (isGrowthPlan || isAdvancedPlan) &&
        lead.chat &&
        lead.chat?.integration !== eChannelType.INSTAGRAM &&
        lead.chat?.integration !== eChannelType.MESSENGER &&
        lead.chat?.integration !== eChannelType.TIKTOK
      )
        return {
          ...lead,
          chat: {
            ...lead.chat,
            integration: eChannelType.WHATSAPP_API,
          },
        };
      return lead;
    }
  }, [isAdvancedPlan, isGrowthPlan]);

  // BUG-807: `options.isSuperseded` lets a caller running a generational
  // refresh abandon its own writes. Each response below writes straight into
  // `currentFunnel` the moment it lands, so a guard the caller checks before
  // starting the batch cannot stop an older run's per-stage response from
  // landing last and overwriting a newer board — the check has to sit next to
  // the write. Third argument is an options object per the max-two-positional
  // -parameters convention.
  const getMoreLeads = useCallback(async (stageId: string, page: number, options?: { isSuperseded?: () => boolean }) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return null;
      const requestedViewKey = boardSelectionViewKey;

      let response;
      let usesCursor = false;

      // INT-1596 (D8): the inbox and the unclassified leads are outside the
      // "No entregados" queue. With the chip on, Stage shows them empty, so
      // they are not asked for; their state is left as it was for when the
      // chip goes off.
      if (
        isUndeliveredFilterOn &&
        (stageId === NIL_UUID || stageId === UNCLASSIFIED_UUID)
      ) {
        return null;
      }

      if (stageId === UNCLASSIFIED_UUID) {
        response = await PipeService.getUnclassifiedLeads(
          currentWorkspaceId,
          currentFunnel.id,
          page,
        );
      } else {
        usesCursor = hasActiveBoardFilters(boardFilters) || stageId === NIL_UUID;
        const stageCursor = currentFunnel.stages.find(
          (stage) => stage.id === stageId,
        )?.nextCursor;
        // A later page without a cursor means the cursor was lost, not that
        // the list ended: the last page sets `isLastPage` and never asks again.
        // Reload page 1 to get a fresh cursor instead of silently stopping
        // without a request (BUG-974).
        if (usesCursor && page > 1 && !stageCursor) {
          return getMoreLeads(stageId, 1, options);
        }

        const queryParams: tGetLeadsQueryParams | undefined = {
          active: true,
          ...(usesCursor
            ? {
                paginationMode: 'cursor' as const,
                ...(page === 1
                  ? { includeTotal: true }
                  : { cursor: stageCursor }),
              }
            : { page }),
          ...(stageId === NIL_UUID ? { inbox: true } : { stageID: stageId }),
          ...buildBoardFilterParams(boardFilters),
          ...(isUndeliveredFilterOn && { undelivered: true as const }),
          inStage: !usesCursor,
        };

        response = await PipeService.getMoreLeads(
          currentWorkspaceId,
          queryParams,
        );
      }

      if (!response || !response.data) {
        throw new Error('No se recibió data del nuevo embudo creado');
      }

      if (options?.isSuperseded?.()) return null;
      setCurrentFunnel((prevFunnel) => {
        if (!prevFunnel || !Array.isArray(prevFunnel.stages)) {
          return null;
        }

        const newStages = prevFunnel.stages.map((stage) => {
          if (stage.id === stageId) {
            const newLeadsFromResponse = (response.data.leads || []).map(
              (lead) => processLeadForWhatsappBusiness(lead),
            );
            const newLeadsTotal = response.data.total;

            const priorLeads = stage.leads && page > 1 ? stage.leads : [];
            const known = new Set(priorLeads.map((lead) => lead.id));
            const newLeads = [
              ...priorLeads,
              ...newLeadsFromResponse.filter((lead) => !known.has(lead.id)),
            ];

            const nextStage = {
              ...stage,
              leads: newLeads,
              ...(typeof newLeadsTotal === 'number' && {
                leadCount: newLeadsTotal,
              }),
              nextCursor: response.data.nextCursor,
              cursorPagination: usesCursor,
              ...(page === 1 && {
                bulkSelectionViewKey:
                  typeof newLeadsTotal === 'number' && Number.isFinite(newLeadsTotal)
                    ? requestedViewKey
                    : undefined,
                pageReset: {
                  revision: (stage.pageReset?.revision ?? 0) + 1,
                  isLastPage: response.data.isLastPage,
                },
              }),
            };

            return nextStage;
          }

          return stage;
        });

        return {
          ...prevFunnel,
          stages: newStages,
        };
      });

      // Fetch AI-respond state for all loaded leads in one bulk call so the
      // ✨ IA chip shows correctly on the kanban without opening each chat.
      if (isSmartLeadBotEnabled && currentWorkspaceId) {
        const leadIds = (response.data.leads || [])
          .map((lead) => lead.id)
          .filter((id): id is string => Boolean(id));

        if (leadIds.length > 0) {
          autoRespondService
            .getBulkState(currentWorkspaceId, leadIds)
            .then(({ states }) => {
              const stateMap = new Map(states.map((s) => [s.leadId, { enabled: s.enabled, needsAttention: s.needsAttention }]));
              if (options?.isSuperseded?.()) return;
              setCurrentFunnel((prev) => {
                if (!prev) return null;
                return {
                  ...prev,
                  stages: prev.stages.map((stage) =>
                    stage.id !== stageId
                      ? stage
                      : {
                          ...stage,
                          leads: (stage.leads ?? []).map((lead) => {
                            const state = stateMap.get(lead.id);
                            if (!state) return lead;
                            return { ...lead, chat: lead.chat ? { ...lead.chat, aiRespondEnabled: state.enabled } : null };
                          }),
                        },
                  ),
                };
              });
              // Seed PENDIENTE chips for leads with expired/escalated sessions.
              // Skip tombstoned ids so a local take-control isn't resurrected by a
              // stale needsAttention flag on lazy-load of a stage.
              const attention = new Set<string>();
              for (const [leadId, state] of stateMap) {
                if (state.needsAttention && !wasLeadRecentlyCleared(leadId)) attention.add(leadId);
              }
              if (attention.size > 0) {
                setSlbEscalatedLeadIds((prev) => {
                  const merged = new Set(prev);
                  for (const id of attention) merged.add(id);
                  return merged;
                });
              }
              // RET-1182: seed the EXPLICIT engagement-state map for leads brought in
              // per-stage (initial render + pagination). Without this, only the combined
              // fetchSlbBulkState() on full-funnel load populated slbEngagementByLead, so
              // paged-in leads fell back to the legacy stage-gated chip path and every
              // PAUSED_* (Transferido) chip was suppressed outside a bot stage.
              setSlbEngagementByLead((prev) => mergeEngagementByLead(prev, states));
            })
            .catch(() => {/* non-critical — chip just won't show until chat is opened */});
        }
      }

      // BUG-569: an empty page is terminal regardless of what the server's
      // isLastPage claims — with a drifted stages.total_leads counter the
      // server-computed last_page can promise pages that no longer exist
      // (totals shrinking mid-scroll, phantom stages), which would otherwise
      // leave the column's IntersectionObserver re-fetching empty pages
      // forever.
      return (
        response.data.isLastPage || (response.data.leads || []).length === 0
      );
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer mas leads',
      });
      return null;
    }
  }, [addAlert, boardFilters, boardSelectionViewKey, currentFunnel, currentUserId, currentWorkspaceId, isSmartLeadBotEnabled, isUndeliveredFilterOn, processLeadForWhatsappBusiness, setCurrentFunnel, setSlbEngagementByLead, setSlbEscalatedLeadIds, wasLeadRecentlyCleared]);

  const getSpecificLeadsWithData = useCallback(async (query: string, page: number, stageID: string) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return null;

      const queryParams: tGetLeadsQueryParams = {
        page,
        query: query,
        stageID: stageID,
      };

      const response = await PipeService.getLeadsBySearch(
        currentWorkspaceId,
        queryParams,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió data del nuevo embudo creado');
      }

      return response.data;
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer leads específicos',
      });
      return null;
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId]);

  const getSpecificLeads = useCallback(async (query: string, page: number, stageID: string) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return null;

      const queryParams: tGetLeadsQueryParams = {
        page,
        query: query,
        stageID: stageID,
      };

      const response = await PipeService.getLeadsBySearch(
        currentWorkspaceId,
        queryParams,
      );

      if (!response || !response.data) {
        throw new Error('No se recibió data del nuevo embudo creado');
      }

      return response.data.leads;
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer leads específicos',
      });
      return null;
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId]);

  const createNewLead = useCallback(async (formData: tCreateLeadForm) => {
    try {
      if (!currentFunnel || !currentWorkspaceId || !currentUserId) return;

      if (formData.countryCode === '52' && formData.handle.length != 10) {
        addAlert({
          type: eAlertType.ERROR,
          message: 'Ingrese el Número telefónico a 10 dígitos',
        });
        return;
      }

      // Shared normalization (owns the MX 521 rule) — same code path the CSV
      // import uses, so both flows store identical handles.
      const handleResult = buildLeadHandle(
        formData.countryCode,
        formData.handle,
      );

      if (!handleResult.valid) {
        addAlert({
          type: eAlertType.ERROR,
          message: 'El número telefónico no es válido para el país elegido',
        });
        return;
      }

      const handleValue = handleResult.handle;

      let channelType: eChannelType = eChannelType.WHATSAPP_WEB;
      if (isAdvancedPlan || isGrowthPlan) {
        channelType = eChannelType.WHATSAPP_API;
      }

      const request: tCreateLeadRequest = {
        full_name: formData.leadName,
        funnel_uuid: formData.funnel,
        handle: handleValue,
        origin: eLeadOrigin.CREATED,
        channel_type: channelType,
      };

      const response = await PipeService.createLead(
        currentWorkspaceId,
        currentUserId,
        request,
      );

      if (response.data) {
        const createdLead = response.data;
        const newLead = funnelLeadParser(
          createdLead as unknown as tLead,
          defaultChannel,
        );

        setCurrentFunnel((prevFunnel) => {
          if (!prevFunnel) return null;

          const targetStageId = createdLead.stage?.uuid;
          const newStages = prevFunnel.stages.map((stage) => {
            if (stage.id !== targetStageId) return stage;

            const prevLeads = stage.leads ?? [];
            const alreadyExists = prevLeads.some(
              (lead) => lead.id === newLead.id,
            );
            if (alreadyExists) return stage;

            return {
              ...stage,
              leads: [newLead, ...prevLeads],
              leadCount: (stage.leadCount ?? 0) + 1,
            };
          });

          return { ...prevFunnel, stages: newStages };
        });
      }

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Lead creado',
      });
    } catch (err) {
      if ((err as AxiosError).response?.status === 422) {
        addAlert({
          type: eAlertType.ERROR,
          message: 'Lead ya existente, consulta con el administrador',
        });
      } else {
        addAlert({
          type: eAlertType.ERROR,
          message:
            (err as Error).message ||
            'Error al acceder al servidor para traer mas leads',
        });
      }
    }
  }, [addAlert, currentFunnel, currentUserId, currentWorkspaceId, defaultChannel, isAdvancedPlan, isGrowthPlan, setCurrentFunnel]);

  const fetchOrGetTemplates = useCallback(async (
    workspaceId: string,
    shouldFetch: boolean,
    setDropdownOptions: React.Dispatch<React.SetStateAction<DropdownOption[]>>,
    handle?: string,
  ) => {
    try {
      const { templates }: tFetchTemplatesResponse = shouldFetch
        ? await PipeService.fetchTemplates(workspaceId)
        : await PipeService.getTemplates(workspaceId, handle);

      setDropdownOptions(
        templates.map((template) => ({
          label: template.name,
          value: template.template_id,
          lang: template.lang,
        })),
      );

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Plantillas sincronizadas correctamente',
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: 'Error al obtener o sincronizar plantillas: ' + error,
      });
    }
  }, [addAlert]);

  const getTemplates = useCallback(async (workspaceId: string, handle?: string) => {
    try {
      const { templates, predefined_variables }: tFetchTemplatesResponse =
        await PipeService.getTemplates(workspaceId, handle);

      return {
        templates: templates as tTemplates[],
        predefinedVariables: predefined_variables,
      };
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: 'Error al obtener plantillas: ' + error,
      });
    }
  }, [addAlert]);

  const handleUpgradeOrIncreaseLimit = useCallback(() => {
    // If user is on basic or advanced plan, open Lead Limit Addon modal
    // Otherwise, show plans view for upgrading to a higher plan
    if (isBusinessPlan || isAdvancedPlan) {
      navigate(
        `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&openLeadLimitAddon=true`,
      );
    } else {
      navigate(
        `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&upgrade=true`,
      );
    }
  }, [isAdvancedPlan, isBusinessPlan, navigate]);

  const createCampaignStage = useCallback(async (
    workspaceId: string,
    templateId: string,
    languageCode: string,
    stageUuid: string,
    sendAt: string,
    parameters?: tWapiTemplateParameter[],
    media?: tWapiMedia,
    name?: string | null,
    stages?: string[] | null,
    description?: string | null,
  ) => {
    try {
      const createCampaignStagePayload = {
        template_id: templateId,
        language_code: languageCode,
        stage_uuid: stageUuid,
        user_uuid: currentUser?.uuid,
        send_at: sendAt,
        parameters: parameters,
        media: media,
        name: name ?? null,
        stages: stages ?? null,
        description: description ?? null,
      };

      await PipeService.createCampaignStage(
        workspaceId,
        createCampaignStagePayload,
      );
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Campaña de etapa creada correctamente',
      });
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Pipe.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al crear la campaña de etapa',
        error as Error,
      );
      // BUG-569: rethrow so the bulk modal's catch owns the user-facing
      // handling — a wba-10024 credit rejection renders inline and keeps the
      // modal open instead of being swallowed here as a toast.
      throw error;
    }
  }, [addAlert, currentUser]);

  const createCampaignLeads = useCallback(async (
    workspaceId: string,
    templateId: string,
    languageCode: string,
    leads: string[],
    sendAt: string,
    parameters?: tWapiTemplateParameter[],
    media?: tWapiMedia,
    name?: string | null,
    stages?: string[] | null,
    description?: string | null,
  ) => {
    try {
      const createCampaignLeadsPayload = {
        template_id: templateId,
        language_code: languageCode,
        user_uuid: currentUser?.uuid,
        leads: leads,
        send_at: sendAt,
        parameters: parameters,
        media: media,
        name: name ?? null,
        stages: stages ?? null,
        description: description ?? null,
      };

      await PipeService.createCampaignLeads(
        workspaceId,
        createCampaignLeadsPayload,
      );
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Campaña de leads creada correctamente',
      });
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Pipe.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al crear la campaña de leads',
        error as Error,
      );
      // BUG-569: rethrow for the same reason as createCampaignStage — the
      // bulk modal needs the failure to keep itself open on credit rejections
      // and to stop firing success analytics on failed sends.
      throw error;
    }
  }, [addAlert, currentUser]);

  return useMemo(() => ({
    currentFunnel,
    minuteKey: Math.floor(now / STAGNANT_TICK_INTERVAL_MS),
    /** U8 — ids of every card that is red right now (fetched flag OR the tick). */
    expiredLeadIds: boardExpiredState.expiredLeadIds,
    /** U8 — the first red card in board order; null while none is. */
    boardExpiredLead: firstExpired,
    loading,
    editMode,
    selectedStage,
    deleteStageModalIsOpen,
    autoAssignModalIsOpen,
    stuckLeadsModalIsOpen,
    bulkMessageWABModalIsOpen,
    deleteFunnelModalIsOpen,
    loadingStagesForDeleteFunnelModal,
    funnelsWithStages,
    workspaceFunnels,
    createNewFunnelMode,
    loadingFilteredLeads,
    isUndeliveredEnabled,
    isUndeliveredFilterOn,
    undeliveredCount,
    selection,
    bulkOperations,
    dispatchSelection,
    applyBulkSelectionResult,
    areStageLeadCountsLoaded: areStageLeadCountsLoaded({
      leadCountsLoadedFunnelId,
      currentFunnelId: currentFunnel?.id,
      stageCount: currentFunnel?.stages?.length ?? 0,
    }),
    onDragEnd,
    addStageInIndex,
    removeStage,
    updateFunnelName,
    updateFunnelDescription,
    updateFunnelEmoji,
    toggleEditMode,
    updateFunnelOnEditMode,
    setSelectedStage,
    setDeleteStageModalIsOpen,
    setAutoAssignModalIsOpen,
    updateAutoAssignUserStatus,
    setStuckLeadsModalIsOpen,
    setBulkMessageWABModalIsOpen,
    updateExpiresAfterValue,
    updateStageName,
    setDeleteFunnelModalIsOpen,
    createNewFunnel,
    removeFunnel,
    setFunnelsWithStages,
    updateFunnel,
    setWorkspaceFunnels,
    setCreateNewFunnelMode,
    getFunnelsWithStages,
    getMoreLeads,
    createNewLead,
    getSelectedFunnelInfo,
    fetchOrGetTemplates,
    getSpecificLeads,
    getSpecificLeadsWithData,
    createCampaignStage,
    createCampaignLeads,
    getTemplates,
    handleUpgradeOrIncreaseLimit,
    stageDescriptionModalIsOpen,
    setStageDescriptionModalIsOpen,
    createStageModalIsOpen,
    setCreateStageModalIsOpen,
    createStageAtIndex,
    setCreateStageAtIndex,
    updateStageConfiguration,
    slbWorkspaceEnabled,
    setSlbWorkspaceEnabled,
    slbHasActiveIntegration,
    slbConfiguredFunnelId,
    slbActiveFunnelIds,
    slbEscalatedLeadIds,
    slbAcknowledgedLeadIds,
    acknowledgeSlbEscalatedLead,
    slbEngagementByLead,
    slbLeadChatMap,
    updateLeadAiRespondState,
    moveLeadToStage,
    removeLeadFromFunnel,
    updateLeadAssignee,
    onLeadTagsChanged,
    cardViewMode: cardViewMode ?? 'compact',
    setCardViewMode,
  }), [
    acknowledgeSlbEscalatedLead,
    addStageInIndex,
    applyBulkSelectionResult,
    autoAssignModalIsOpen,
    boardExpiredState,
    bulkMessageWABModalIsOpen,
    bulkOperations,
    cardViewMode,
    createCampaignLeads,
    createCampaignStage,
    createNewFunnel,
    createNewFunnelMode,
    createNewLead,
    createStageAtIndex,
    createStageModalIsOpen,
    currentFunnel,
    deleteFunnelModalIsOpen,
    deleteStageModalIsOpen,
    dispatchSelection,
    editMode,
    fetchOrGetTemplates,
    firstExpired,
    funnelsWithStages,
    getFunnelsWithStages,
    getMoreLeads,
    getSelectedFunnelInfo,
    getSpecificLeads,
    getSpecificLeadsWithData,
    getTemplates,
    handleUpgradeOrIncreaseLimit,
    isUndeliveredEnabled,
    isUndeliveredFilterOn,
    leadCountsLoadedFunnelId,
    loading,
    loadingFilteredLeads,
    loadingStagesForDeleteFunnelModal,
    moveLeadToStage,
    now,
    onDragEnd,
    removeFunnel,
    removeLeadFromFunnel,
    removeStage,
    onLeadTagsChanged,
    updateLeadAssignee,
    selectedStage,
    selection,
    setAutoAssignModalIsOpen,
    setBulkMessageWABModalIsOpen,
    setCardViewMode,
    setCreateNewFunnelMode,
    setCreateStageAtIndex,
    setCreateStageModalIsOpen,
    setDeleteFunnelModalIsOpen,
    setDeleteStageModalIsOpen,
    setFunnelsWithStages,
    setSelectedStage,
    setSlbWorkspaceEnabled,
    setStageDescriptionModalIsOpen,
    setStuckLeadsModalIsOpen,
    setWorkspaceFunnels,
    slbAcknowledgedLeadIds,
    slbActiveFunnelIds,
    slbConfiguredFunnelId,
    slbEngagementByLead,
    slbEscalatedLeadIds,
    slbHasActiveIntegration,
    slbLeadChatMap,
    slbWorkspaceEnabled,
    stageDescriptionModalIsOpen,
    stuckLeadsModalIsOpen,
    toggleEditMode,
    undeliveredCount,
    updateAutoAssignUserStatus,
    updateExpiresAfterValue,
    updateFunnel,
    updateFunnelDescription,
    updateFunnelEmoji,
    updateFunnelName,
    updateFunnelOnEditMode,
    updateLeadAiRespondState,
    updateStageConfiguration,
    updateStageName,
    workspaceFunnels
  ]);
}
